import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Input, Button, Avatar, message, Spin, Space, Typography, Divider, Card, Form, Select, Upload, Modal, Tooltip, Tabs, Slider } from 'antd';
import { SendOutlined, RobotOutlined, ToolOutlined, ArrowLeftOutlined, CloseOutlined, PlusOutlined, UploadOutlined, PaperClipOutlined, DeleteOutlined, QuestionCircleOutlined, InboxOutlined, PlayCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import api, { conversationApi, authApi, modelConfigApi, workflowModelApi, userPointsApi, apiTaskApi } from '../services/api';
import { ApiResponse, CallGroup } from '../types';
import { useTaskMonitor } from '../hooks/useTaskMonitor';
import TaskCard from '../components/TaskCard';
import SessionSidebar from '../components/SessionSidebar';
import '../styles/modern-theme.css';
import './AIConversationPageModern.css';
import '../styles/ai-conversation-nexus.css';
import RetryImage from '../components/RetryImage';
const { TextArea } = Input;
const { Title, Text } = Typography;

interface Message {
  role: 'user' | 'assistant' | 'task_card';
  content?: string;
  timestamp: string;
  image_urls?: string[]; // 多张图片URL数组
  // 任务卡片专用字段
  task_id?: string;
  task_name?: string;
  status?: 'processing' | 'success' | 'fail' | 'cleaned';
  result_info?: string;
  error_msg?: string;
  created_at?: string;
  params?: Record<string, string> | null; // 任务创建时的参数摘要（文件只保留文件名）
}

interface MentionItem {
  id: number;
  mention_tag: string;
  description: string;
  icon_url?: string;
  model_config: {
    model_id: string;
    model_name: string;
    model_type: string;
    display_name: string;
    cost_type: 'free' | 'fixed' | 'dynamic';
    cost_points: number;
  };
}

// 单个组件配置
interface ComponentConfig {
  name: string;
  component: 'textbox' | 'dropdown' | 'upload' | 'video_coordinate_picker' | 'video_region_selector' | 'video_coordinate';
  variable: string;
  input_type?: string;
  required?: boolean;
  default_text?: string;
  values?: Array<{ label: string; value: string }> | string;
  options?: Array<{ label: string; value: string }> | string;
  type?: string;
  file_size?: number;
  batch_process?: boolean;
  longest?: number;
  coordinate_label?: string;
}

interface VideoCoordinatePickerState {
  uploadVariable: string;
  coordinateVariable: string;
  videoUrl: string;
  fileName: string;
  sourceWidth: number;
  sourceHeight: number;
  normalizedWidth: number;
  normalizedHeight: number;
  longest: number;
}

interface VideoSelectionRect {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

interface VideoRegionMoveState {
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
}

type VideoResizeHandle = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

// 多步骤工作流页面配置
interface WorkflowStepConfig {
  page_id: string;
  title: string;
  workflow_id: string;
  fields: ComponentConfig[];
}

// 工作流关联关系
interface MarketWorkflowRel {
  id: number;
  market_id: string;
  wf_id: string;
  step_key: string;
  step_order: number;
}

interface WorkflowInfo {
  market_id: string;
  display_name: string;
  wf_type: string;
  wf_id: string;
  component_template: ComponentConfig[] | WorkflowStepConfig[];
  api_params_template: any[];
  instructions?: string;
  model_type?: 'opensource' | 'closedsource' | 'apisource';
  access_type?: 'pro' | 'trial';
  workflow_rels?: MarketWorkflowRel[];
}

/**
 * 获取 apisource 渠道的预扣积分。
 */
const getCallGroupPreDeductedPoints = (group?: CallGroup | null): number => {
  return Math.max(0, Number(group?.pre_deducted_points) || 0);
};

const AIConversationPage: React.FC = () => {
  const { marketId } = useParams<{ marketId: string }>();
  const navigate = useNavigate();
  const { user, refreshUserInfo } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null); // 跟踪哪个会话正在加载
  const [sessionLoadingStates, setSessionLoadingStates] = useState<Map<string, boolean>>(new Map()); // 跟踪每个会话的loading状态
  const [sessionPendingMessages, setSessionPendingMessages] = useState<Map<string, Message[]>>(new Map()); // 跟踪每个会话的待处理消息
  const [workflowInfo, setWorkflowInfo] = useState<WorkflowInfo | null>(null);
  const [componentPanelVisible, setComponentPanelVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File>>({});
  const [uploadedFilePreviews, setUploadedFilePreviews] = useState<Record<string, string>>({});
  const [form] = Form.useForm();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const uploadedFilePreviewsRef = useRef<Record<string, string>>({});
  const uploadedFilePreviewTokensRef = useRef<Record<string, string>>({});
  const [videoCoordinatePicker, setVideoCoordinatePicker] = useState<VideoCoordinatePickerState | null>(null);
  const [videoSelectionRect, setVideoSelectionRect] = useState<VideoSelectionRect | null>(null);
  const [isSelectingVideoRegion, setIsSelectingVideoRegion] = useState(false);
  const [isMovingVideoRegion, setIsMovingVideoRegion] = useState(false);
  const [activeVideoResizeHandle, setActiveVideoResizeHandle] = useState<VideoResizeHandle | null>(null);
  const [isVideoPickerPlaying, setIsVideoPickerPlaying] = useState(false);
  const [videoPickerCurrentTime, setVideoPickerCurrentTime] = useState(0);
  const [videoPickerDuration, setVideoPickerDuration] = useState(0);
  const videoPickerFrameRef = useRef<HTMLDivElement>(null);
  const videoPickerVideoRef = useRef<HTMLVideoElement>(null);
  const videoRegionMoveStateRef = useRef<VideoRegionMoveState | null>(null);
  
  // 会话管理状态
  const [sessions, setSessions] = useState<any[]>([]);
  // 从 sessionStorage 恢复上次选中的会话ID（按 marketId 区分）
  const savedSessionKey = `ai_conv_session_${marketId}`;
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(
    () => sessionStorage.getItem(savedSessionKey)
  );
  const currentSessionIdRef = useRef<string | null>(
    sessionStorage.getItem(savedSessionKey)
  ); // 🔒 使用ref跟踪最新的会话ID，避免异步状态问题
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [mountTrigger, setMountTrigger] = useState(0); // 组件挂载触发器，用于重新订阅任务
  
  // 图片上传相关状态（支持多张图片）
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [maxImages, setMaxImages] = useState(5); // 默认最多5张，从环境变量读取
  
  // @mention下拉列表相关状态
  const [mentionList, setMentionList] = useState<MentionItem[]>([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionFilterText, setMentionFilterText] = useState('');
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0); // 当前选中的mention索引
  const inputRef = useRef<any>(null);
  
  // 使用说明相关状态
  const [isInstructionsModalVisible, setIsInstructionsModalVisible] = useState(false);
  const [instructionsContent, setInstructionsContent] = useState<string>('');
  
  // 闭源模型渠道选择相关状态
  const [availableModels, setAvailableModels] = useState<any[]>([]);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [userPoints, setUserPoints] = useState<number>(0);

  // apisource 渠道分组相关状态
  const [callGroups, setCallGroups] = useState<any[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  
  // 积分扣费/退款已移入工作流 API 内部处理，前端不再需要追踪
  // const chargedTasksRef = useRef<Map<string, { amount: number; model_id: string }>>(new Map());
  
  // 多步骤工作流相关状态
  const [activeStepKey, setActiveStepKey] = useState<string>('step_1');
  
  // 任务监控
  const { subscribeTask } = useTaskMonitor({
    userId: user?.user_id || '',
    onTaskUpdate: (task) => {
      // 更新消息列表中的任务卡片
      setMessages(prev => {
        const taskIndex = prev.findIndex(m => m.role === 'task_card' && m.task_id === task.task_id);
        if (taskIndex >= 0) {
          // 更新现有任务卡片
          const updated = [...prev];
          updated[taskIndex] = {
            ...updated[taskIndex],
            status: task.status,
            result_info: task.result_info,
            error_msg: task.error_msg
          };
          return updated;
        }
        return prev;
      });
    },
    onTaskFail: async (task) => {
      console.log('❌ 任务失败:', task.task_id);
      
      // 积分退款逻辑已移入工作流 API 内部处理
      // 当工作流执行失败时，API 内部会自动调用 /api/v1/wallet/refund 进行退款
      // 前端不再单独处理积分退款，避免重复退款
      // const chargeInfo = chargedTasksRef.current.get(task.task_id);
      // if (chargeInfo) {
      //   // 积分退款逻辑已移至工作流 API 内部
      // }
    },
    onTaskNotFound: (taskId) => {
      // 任务在数据库中不存在，标记为已清理
      console.log('🗑️ 任务不存在，标记为已清理:', taskId);
      handleUrlInvalid(taskId);
    }
  });

  // 判断是否为多步骤工作流格式
  const isMultiStepWorkflow = (template: ComponentConfig[] | WorkflowStepConfig[] | undefined): template is WorkflowStepConfig[] => {
    if (!template || template.length === 0) return false;
    // 检查第一个元素是否包含 page_id 字段
    return 'page_id' in template[0];
  };

  // 获取当前步骤的字段配置
  const getCurrentStepFields = (): ComponentConfig[] => {
    if (!workflowInfo?.component_template) return [];
    
    if (isMultiStepWorkflow(workflowInfo.component_template)) {
      const currentStep = workflowInfo.component_template.find(step => step.page_id === activeStepKey);
      return currentStep?.fields || [];
    }
    
    // 旧格式，直接返回
    return workflowInfo.component_template as ComponentConfig[];
  };

  // 获取当前步骤的工作流ID
  const getCurrentStepWorkflowId = (): string => {
    if (!workflowInfo) return '';
    
    if (isMultiStepWorkflow(workflowInfo.component_template)) {
      const currentStep = workflowInfo.component_template.find(step => step.page_id === activeStepKey);
      if (currentStep) {
        // 从 workflow_rels 中查找对应的 wf_id
        const rel = workflowInfo.workflow_rels?.find(r => r.step_key === activeStepKey);
        return rel?.wf_id || currentStep.workflow_id || '';
      }
    }
    
    // 旧格式，返回默认的 wf_id
    const rel = workflowInfo.workflow_rels?.find(r => r.step_key === 'step_1');
    return rel?.wf_id || workflowInfo.wf_id || '';
  };

  /**
   * Check whether a dynamic component is the video coordinate picker.
   */
  const isVideoCoordinateComponent = (config: ComponentConfig): boolean => {
    return ['video_coordinate_picker', 'video_region_selector', 'video_coordinate'].includes(config.component);
  };

  /**
   * Split a component variable string into one or more backend parameter names.
   */
  const getComponentVariables = (config: ComponentConfig): string[] => {
    return String(config.variable || '')
      .split('|')
      .map((variable) => variable.trim())
      .filter(Boolean);
  };

  /**
   * Return the form fields owned by a dynamic component.
   */
  const getComponentFormFieldNames = (config: ComponentConfig): string[] => {
    const variables = getComponentVariables(config);
    return isVideoCoordinateComponent(config) ? variables.slice(0, 2) : variables.slice(0, 1);
  };

  /**
   * Return the upload field names owned by a dynamic component.
   */
  const getComponentFileFieldNames = (config: ComponentConfig): string[] => {
    const variables = getComponentVariables(config);
    if (config.component === 'upload') {
      return variables.slice(0, 1);
    }
    if (isVideoCoordinateComponent(config)) {
      return variables.slice(0, 1);
    }
    return [];
  };

  /**
   * Resolve the upload and coordinate variables for the video coordinate picker.
   */
  const getVideoCoordinateVariables = (config: ComponentConfig) => {
    const [uploadVariable = '', coordinateVariable = ''] = getComponentVariables(config);
    return { uploadVariable, coordinateVariable };
  };

  /**
   * 判断文件是否为图片。
   */
  const isImageFile = (file: File): boolean => {
    return file.type.startsWith('image/') || /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/i.test(file.name);
  };

  /**
   * 判断文件是否为视频。
   */
  const isVideoFile = (file: File): boolean => {
    return file.type.startsWith('video/') || /\.(mp4|avi|mov|webm|mkv|flv|wmv)$/i.test(file.name);
  };

  /**
   * 释放本地预览 URL。
   */
  const revokePreviewUrl = (previewUrl?: string | null) => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  /**
   * 更新指定字段的本地预览图。
   */
  const updateUploadedFilePreview = (variable: string, previewUrl: string | null) => {
    const nextPreviews = { ...uploadedFilePreviewsRef.current };
    const previousUrl = nextPreviews[variable];

    if (previousUrl && previousUrl !== previewUrl) {
      revokePreviewUrl(previousUrl);
    }

    if (previewUrl) {
      nextPreviews[variable] = previewUrl;
    } else {
      delete nextPreviews[variable];
    }

    uploadedFilePreviewsRef.current = nextPreviews;
    setUploadedFilePreviews(nextPreviews);
  };

  /**
   * 判断画布中的视频帧是否接近黑帧。
   */
  const isCanvasFrameDark = (canvas: HTMLCanvasElement): boolean => {
    const context = canvas.getContext('2d');
    if (!context) {
      return true;
    }

    const sampleWidth = Math.min(64, canvas.width);
    const sampleHeight = Math.min(64, canvas.height);
    const imageData = context.getImageData(0, 0, sampleWidth, sampleHeight).data;
    let totalLuminance = 0;

    for (let i = 0; i < imageData.length; i += 4) {
      totalLuminance += imageData[i] * 0.299 + imageData[i + 1] * 0.587 + imageData[i + 2] * 0.114;
    }

    return totalLuminance / (imageData.length / 4) < 12;
  };

  /**
   * 将画布内容转换成本地预览 URL。
   */
  const createCanvasPreviewUrl = (canvas: HTMLCanvasElement): Promise<string | null> => {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob ? URL.createObjectURL(blob) : null);
      }, 'image/jpeg', 0.9);
    });
  };

  /**
   * 生成视频首帧预览图。
   */
  const generateVideoThumbnail = (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const objectUrl = URL.createObjectURL(file);
      let isCleanedUp = false;

      const cleanup = () => {
        if (isCleanedUp) {
          return;
        }

        isCleanedUp = true;
        video.pause();
        video.removeAttribute('src');
        video.load();
        URL.revokeObjectURL(objectUrl);
      };

      const handleError = () => {
        cleanup();
        resolve(null);
      };

      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';

      video.onloadedmetadata = async () => {
        try {
          if (!video.videoWidth || !video.videoHeight) {
            handleError();
            return;
          }

          const maxDimension = 640;
          const scale = Math.min(1, maxDimension / Math.max(video.videoWidth, video.videoHeight));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
          canvas.height = Math.max(1, Math.round(video.videoHeight * scale));

          const context = canvas.getContext('2d');
          if (!context) {
            handleError();
            return;
          }

          const duration = Number.isFinite(video.duration) ? video.duration : 0;
          const frameStep = 1 / 24;
          const candidateTimes = [
            frameStep * 2,
            frameStep * 3,
            0.2,
            0.5,
            duration * 0.1,
            duration * 0.25,
          ]
            .filter((time) => time > 0 && (!duration || time < duration))
            .map((time) => Math.min(time, Math.max(0, duration - 0.05)));

          for (const seekTime of candidateTimes) {
            await new Promise<void>((seekResolve, seekReject) => {
              video.onseeked = () => seekResolve();
              video.onerror = () => seekReject(new Error('video seek failed'));
              video.currentTime = seekTime;
            });

            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            if (!isCanvasFrameDark(canvas)) {
              const thumbnailUrl = await createCanvasPreviewUrl(canvas);
              cleanup();
              resolve(thumbnailUrl);
              return;
            }
          }

          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const thumbnailUrl = await createCanvasPreviewUrl(canvas);
          cleanup();
          resolve(thumbnailUrl);
        } catch (error) {
          console.error('❌ 生成视频首帧预览失败:', error);
          handleError();
        }
      };

      video.onerror = handleError;
      video.src = objectUrl;
    });
  };

  /**
   * Read the source dimensions of a local video file.
   */
  const getVideoDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const objectUrl = URL.createObjectURL(file);

      const cleanup = () => {
        video.removeAttribute('src');
        video.load();
        URL.revokeObjectURL(objectUrl);
      };

      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        const width = video.videoWidth;
        const height = video.videoHeight;
        cleanup();

        if (!width || !height) {
          reject(new Error('无法读取视频分辨率'));
          return;
        }

        resolve({ width, height });
      };
      video.onerror = () => {
        cleanup();
        reject(new Error('无法读取视频信息'));
      };
      video.src = objectUrl;
    });
  };

  /**
   * Close the video coordinate picker and release its local video URL.
   */
  const closeVideoCoordinatePicker = () => {
    if (videoCoordinatePicker?.videoUrl) {
      URL.revokeObjectURL(videoCoordinatePicker.videoUrl);
    }
    videoPickerVideoRef.current?.pause();
    setVideoCoordinatePicker(null);
    setVideoSelectionRect(null);
    setIsSelectingVideoRegion(false);
    setIsMovingVideoRegion(false);
    setActiveVideoResizeHandle(null);
    setIsVideoPickerPlaying(false);
    setVideoPickerCurrentTime(0);
    setVideoPickerDuration(0);
    videoRegionMoveStateRef.current = null;
  };

  /**
   * Open the coordinate picker modal for a selected local video file.
   */
  const openVideoCoordinatePicker = async (config: ComponentConfig) => {
    const { uploadVariable, coordinateVariable } = getVideoCoordinateVariables(config);
    const file = uploadedFiles[uploadVariable];

    if (!uploadVariable || !coordinateVariable) {
      message.error('视频坐标组件变量配置错误');
      return;
    }

    if (!file) {
      message.warning('请先上传视频文件');
      return;
    }

    if (!isVideoFile(file)) {
      message.error('请选择视频文件后再标定坐标');
      return;
    }

    try {
      const { width, height } = await getVideoDimensions(file);
      const longest = Math.max(1, Number(config.longest) || 1024);
      const scale = longest / Math.max(width, height);

      if (videoCoordinatePicker?.videoUrl) {
        URL.revokeObjectURL(videoCoordinatePicker.videoUrl);
      }

      setVideoCoordinatePicker({
        uploadVariable,
        coordinateVariable,
        videoUrl: URL.createObjectURL(file),
        fileName: file.name,
        sourceWidth: width,
        sourceHeight: height,
        normalizedWidth: width * scale,
        normalizedHeight: height * scale,
        longest
      });
      setVideoSelectionRect(null);
      setIsSelectingVideoRegion(false);
      setIsMovingVideoRegion(false);
      setActiveVideoResizeHandle(null);
      setIsVideoPickerPlaying(false);
      setVideoPickerCurrentTime(0);
      setVideoPickerDuration(0);
      videoRegionMoveStateRef.current = null;
    } catch (error: any) {
      message.error(error?.message || '读取视频信息失败');
    }
  };

  /**
   * Convert a mouse event into coordinates inside the displayed video frame.
   */
  const getVideoFramePoint = (event: React.MouseEvent<HTMLDivElement>) => {
    const frame = videoPickerFrameRef.current;
    if (!frame) {
      return null;
    }

    const rect = frame.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
    const y = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
    return { x, y };
  };

  /**
   * Return normalized rectangle bounds for rendering handles.
   */
  const getVideoSelectionBounds = (rect: VideoSelectionRect) => {
    const left = Math.min(rect.startX, rect.endX);
    const top = Math.min(rect.startY, rect.endY);
    const right = Math.max(rect.startX, rect.endX);
    const bottom = Math.max(rect.startY, rect.endY);
    return {
      left,
      top,
      right,
      bottom,
      width: right - left,
      height: bottom - top
    };
  };

  /**
   * Start drawing a subtitle region on the displayed video.
   */
  const handleVideoRegionMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }

    const point = getVideoFramePoint(event);
    if (!point) {
      return;
    }

    event.preventDefault();
    setActiveVideoResizeHandle(null);
    setIsMovingVideoRegion(false);
    videoRegionMoveStateRef.current = null;
    setIsSelectingVideoRegion(true);
    setVideoSelectionRect({
      startX: point.x,
      startY: point.y,
      endX: point.x,
      endY: point.y
    });
  };

  /**
   * Update the subtitle region while dragging over the video.
   */
  const handleVideoRegionMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelectingVideoRegion && !isMovingVideoRegion) {
      return;
    }

    const point = getVideoFramePoint(event);
    if (!point) {
      return;
    }

    if (isMovingVideoRegion) {
      const moveState = videoRegionMoveStateRef.current;
      const frame = videoPickerFrameRef.current;
      if (!moveState || !frame) {
        return;
      }

      const frameRect = frame.getBoundingClientRect();
      const maxLeft = Math.max(0, frameRect.width - moveState.width);
      const maxTop = Math.max(0, frameRect.height - moveState.height);
      const left = Math.max(0, Math.min(maxLeft, point.x - moveState.offsetX));
      const top = Math.max(0, Math.min(maxTop, point.y - moveState.offsetY));

      setVideoSelectionRect({
        startX: left,
        startY: top,
        endX: left + moveState.width,
        endY: top + moveState.height
      });
      return;
    }

    setVideoSelectionRect((prev) => {
      if (!prev) {
        return prev;
      }

      if (!activeVideoResizeHandle) {
        return {
          ...prev,
          endX: point.x,
          endY: point.y
        };
      }

      const bounds = getVideoSelectionBounds(prev);
      const nextRect: VideoSelectionRect = {
        startX: bounds.left,
        startY: bounds.top,
        endX: bounds.right,
        endY: bounds.bottom
      };

      if (activeVideoResizeHandle === 'top-left') {
        nextRect.startX = point.x;
        nextRect.startY = point.y;
      } else if (activeVideoResizeHandle === 'top-right') {
        nextRect.endX = point.x;
        nextRect.startY = point.y;
      } else if (activeVideoResizeHandle === 'bottom-left') {
        nextRect.startX = point.x;
        nextRect.endY = point.y;
      } else {
        nextRect.endX = point.x;
        nextRect.endY = point.y;
      }

      return nextRect;
    });
  };

  /**
   * Finish drawing the subtitle region.
   */
  const handleVideoRegionMouseUp = () => {
    setIsSelectingVideoRegion(false);
    setIsMovingVideoRegion(false);
    setActiveVideoResizeHandle(null);
    videoRegionMoveStateRef.current = null;
  };

  /**
   * Start moving the selected region within the displayed video bounds.
   */
  const handleVideoSelectionMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const point = getVideoFramePoint(event);
    if (!point || !videoSelectionRect) {
      return;
    }

    const bounds = getVideoSelectionBounds(videoSelectionRect);
    setVideoSelectionRect({
      startX: bounds.left,
      startY: bounds.top,
      endX: bounds.right,
      endY: bounds.bottom
    });
    videoRegionMoveStateRef.current = {
      offsetX: point.x - bounds.left,
      offsetY: point.y - bounds.top,
      width: bounds.width,
      height: bounds.height
    };
    setActiveVideoResizeHandle(null);
    setIsSelectingVideoRegion(false);
    setIsMovingVideoRegion(true);
  };

  /**
   * Start resizing the selected region from one of its corner handles.
   */
  const handleVideoResizeHandleMouseDown = (event: React.MouseEvent<HTMLDivElement>, handle: VideoResizeHandle) => {
    event.preventDefault();
    event.stopPropagation();

    if (!videoSelectionRect) {
      return;
    }

    const bounds = getVideoSelectionBounds(videoSelectionRect);
    setVideoSelectionRect({
      startX: bounds.left,
      startY: bounds.top,
      endX: bounds.right,
      endY: bounds.bottom
    });
    setActiveVideoResizeHandle(handle);
    setIsMovingVideoRegion(false);
    videoRegionMoveStateRef.current = null;
    setIsSelectingVideoRegion(true);
  };

  /**
   * Toggle playback for the coordinate picker video.
   */
  const toggleVideoPickerPlayback = () => {
    const video = videoPickerVideoRef.current;
    if (!video) {
      return;
    }

    if (video.paused) {
      video.play().catch(() => {
        message.warning('视频播放失败，请重试');
      });
      return;
    }

    video.pause();
  };

  /**
   * Seek the coordinate picker video to a selected time.
   */
  const seekVideoPicker = (value: number) => {
    const video = videoPickerVideoRef.current;
    if (!video) {
      return;
    }

    video.currentTime = value;
    setVideoPickerCurrentTime(value);
  };

  /**
   * Format video seconds as mm:ss text.
   */
  const formatVideoPickerTime = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds <= 0) {
      return '0:00';
    }

    const minutes = Math.floor(seconds / 60);
    const restSeconds = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${minutes}:${restSeconds}`;
  };

  /**
   * Save the selected region as a longest-side-normalized coordinate JSON string.
   */
  const confirmVideoCoordinateSelection = () => {
    if (!videoCoordinatePicker || !videoSelectionRect || !videoPickerFrameRef.current) {
      message.warning('请先在视频上框选字幕区域');
      return;
    }

    const frameRect = videoPickerFrameRef.current.getBoundingClientRect();
    const selectedWidth = Math.abs(videoSelectionRect.endX - videoSelectionRect.startX);
    const selectedHeight = Math.abs(videoSelectionRect.endY - videoSelectionRect.startY);

    if (selectedWidth < 4 || selectedHeight < 4) {
      message.warning('框选区域太小，请重新选择');
      return;
    }

    const scaleX = videoCoordinatePicker.normalizedWidth / frameRect.width;
    const scaleY = videoCoordinatePicker.normalizedHeight / frameRect.height;
    const coordinate = [{
      startX: videoSelectionRect.startX * scaleX,
      startY: videoSelectionRect.startY * scaleY,
      endX: videoSelectionRect.endX * scaleX,
      endY: videoSelectionRect.endY * scaleY
    }];

    form.setFieldsValue({
      [videoCoordinatePicker.coordinateVariable]: JSON.stringify(coordinate)
    });
    message.success('字幕区域坐标已生成');
    closeVideoCoordinatePicker();
  };

  /**
   * Clear selected workflow files, previews, and any open video coordinate picker.
   */
  const clearUploadedWorkflowFiles = () => {
    Object.values(uploadedFilePreviewsRef.current).forEach((previewUrl) => {
      revokePreviewUrl(previewUrl);
    });
    if (videoCoordinatePicker?.videoUrl) {
      URL.revokeObjectURL(videoCoordinatePicker.videoUrl);
    }
    uploadedFilePreviewsRef.current = {};
    uploadedFilePreviewTokensRef.current = {};
    setUploadedFilePreviews({});
    setUploadedFiles({});
    setVideoCoordinatePicker(null);
    setVideoSelectionRect(null);
    setIsSelectingVideoRegion(false);
    setActiveVideoResizeHandle(null);
    setIsVideoPickerPlaying(false);
    setVideoPickerCurrentTime(0);
    setVideoPickerDuration(0);
  };

  // 滚动到底部
  const scrollToBottom = () => {
    // 方法1: 使用scrollIntoView
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
    
    // 方法2: 直接操作messages-container的scrollTop
    const messagesContainer = document.querySelector('.messages-container');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  };

  useEffect(() => {
    // 延迟执行以确保DOM已完全渲染
    const timer = setTimeout(() => scrollToBottom(), 150);
    return () => clearTimeout(timer);
  }, [messages]);

  useEffect(() => {
    return () => {
      Object.values(uploadedFilePreviewsRef.current).forEach((previewUrl) => {
        revokePreviewUrl(previewUrl);
      });
    };
  }, []);

  // 初始化：读取环境变量配置
  useEffect(() => {
    // 从环境变量读取最大图片数量
    const maxImagesFromEnv = import.meta.env.VITE_MAX_IMAGES || 5;
    setMaxImages(Number(maxImagesFromEnv));
    console.log('📸 最大图片数量配置:', maxImagesFromEnv);
  }, []);

  // 加载工作流信息和会话列表（只在组件挂载和 marketId/userId 变化时执行）
  useEffect(() => {
    const userId = user?.user_id;
    if (marketId && userId) {
      console.log('🔄 加载对话数据: marketId=', marketId, ', userId=', userId);
      loadWorkflowInfo();
      loadSessions();
      loadMentionList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketId, user?.user_id]);

  // 组件挂载时自增触发器
  useEffect(() => {
    setMountTrigger(prev => prev + 1);
  }, []);

  // 监听挂载触发器和会话，重新订阅任务（解决页面切换后WebSocket不更新的问题）
  useEffect(() => {
    if (currentSessionId && user?.user_id && mountTrigger > 0) {
      console.log('🔄 会话就绪，检查处理中的任务');
      // 使用延迟确保loadSessions已执行
      const timer = setTimeout(() => {
        resubscribeProcessingTasks();
      }, 500);
      
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId, mountTrigger]);

  const loadWorkflowInfo = async () => {
    try {
      const response = await conversationApi.getWorkflowInfo(marketId!);
      console.log('🔍 工作流信息响应:', response);
      if (response.success) {
        console.log('✅ 工作流信息数据:', response.data);
        console.log('📦 component_template:', response.data.component_template);
        console.log('📦 component_template 类型:', typeof response.data.component_template);
        console.log('📦 component_template 长度:', response.data.component_template?.length);
        setWorkflowInfo(response.data);
        
        // 加载使用说明
        if (response.data.instructions) {
          setInstructionsContent(response.data.instructions);
        }
        
        // 如果是闭源模型，加载模型渠道列表和用户积分
        if (response.data.model_type === 'closedsource') {
          console.log('🔍 检测到闭源模型，加载模型渠道列表和用户积分');
          await loadAvailableModels();
          await loadUserPoints();
        }
        // 如果是 apisource，加载 call-groups 和用户积分
        if (response.data.model_type === 'apisource') {
          console.log('🔍 检测到 API 模型，加载渠道分组列表和用户积分');
          await loadCallGroups(response.data);
          await loadUserPoints();
        }
      }
    } catch (error: any) {
      if (error?.response?.status === 403) {
        message.warning(error.response.data?.message || '当前角色无权访问该工作流');
        navigate('/workflow-market', { replace: true });
        return;
      }
      console.error('❌ 加载工作流信息失败:', error);
      message.error('加载工作流信息失败');
    }
  };

  // 加载mention列表
  const loadMentionList = async () => {
    try {
      const response = await modelConfigApi.getMentions();
      
      // 响应拦截器已经返回了response.data，所以response就是{success: true, data: [...]}
      if (response.success && response.data && Array.isArray(response.data)) {
        setMentionList(response.data);
        console.log('✅ 加载mention列表成功，数量:', response.data.length);
      } else {
        console.error('❌ mention列表返回失败:', response);
        message.error('加载模型列表失败');
      }
    } catch (error) {
      console.error('❌ 加载mention列表失败:', error);
      message.error('加载模型列表失败');
    }
  };

  // 加载 apisource call-groups
  const loadCallGroups = async (wfInfo?: any) => {
    try {
      const info = wfInfo || workflowInfo;
      // 从 api_params_template 中读取 modality
      let modality: string | undefined;
      if (info?.api_params_template && Array.isArray(info.api_params_template)) {
        const modalityParam = info.api_params_template.find((p: any) => p.input_key === 'modality');
        if (modalityParam?.value) modality = modalityParam.value;
      }
      const response = await apiTaskApi.getCallGroups(modality);
      if (response.success && response.data) {
        setCallGroups(response.data);
        // 自动选中第一个
        if (response.data.length > 0 && !selectedGroupId) {
          setSelectedGroupId(response.data[0].group_id);
        }
        console.log('✅ 加载 call-groups 成功，数量:', response.data.length);
      }
    } catch (error) {
      console.error('❌ 加载 call-groups 失败:', error);
    }
  };

  // 加载闭源模型渠道列表
  const loadAvailableModels = async () => {
    if (!marketId) return;
    
    try {
      const response = await workflowModelApi.getMarketModels(marketId);
      if (response.success && response.data) {
        setAvailableModels(response.data);
        // 自动选择默认模型
        const defaultModel = response.data.find((m: any) => m.is_default);
        if (defaultModel) {
          setSelectedModelId(defaultModel.model_id);
        } else if (response.data.length > 0) {
          setSelectedModelId(response.data[0].model_id);
        }
        console.log('✅ 加载模型渠道列表成功，数量:', response.data.length);
      }
    } catch (error) {
      console.error('❌ 加载模型渠道列表失败:', error);
    }
  };

  // 加载用户积分信息
  const loadUserPoints = async () => {
    if (!user?.user_id) return;
    
    try {
      const response = await userPointsApi.getUserPoints(user.user_id);
      if (response.success && response.data) {
        setUserPoints(response.data.points || 0);
        console.log('✅ 加载用户积分成功:', response.data.points);
      }
    } catch (error) {
      console.error('❌ 加载用户积分失败:', error);
    }
  };

  // 重新订阅处理中的任务（解决页面切换后WebSocket不更新的问题）
  const resubscribeProcessingTasks = async () => {
    if (!currentSessionId) return;
    
    try {
      const response = await conversationApi.getSessionHistory(currentSessionId);
      
      if (response.success && response.data) {
        const processingTasks = response.data.filter(
          (msg: any) => msg.role === 'task_card' && msg.status === 'processing'
        );
        
        if (processingTasks.length > 0) {
          const taskIds = processingTasks.map((msg: any) => msg.task_id);
          console.log(`🔍 检查 ${taskIds.length} 个处理中的任务是否存在`);
          
          // 先检查任务是否存在
          const checkResponse = await conversationApi.checkTasksExist(taskIds);
          
          if (checkResponse.success && checkResponse.data) {
            const existingTaskIds: string[] = [];
            const nonExistingTaskIds: string[] = [];
            
            checkResponse.data.forEach((item: { task_id: string; exists: boolean }) => {
              if (item.exists) {
                existingTaskIds.push(item.task_id);
              } else {
                nonExistingTaskIds.push(item.task_id);
              }
            });
            
            // 标记不存在的任务为已清理
            if (nonExistingTaskIds.length > 0) {
              console.log(`🗑️ ${nonExistingTaskIds.length} 个任务不存在，标记为已清理`);
              for (const taskId of nonExistingTaskIds) {
                await handleUrlInvalid(taskId);
              }
            }
            
            // 只订阅存在的任务
            if (existingTaskIds.length > 0) {
              console.log(`📌 重新订阅 ${existingTaskIds.length} 个处理中的任务`);
              existingTaskIds.forEach((taskId: string) => {
                subscribeTask(taskId);
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ 重新订阅任务失败:', error);
    }
  };

  // 加载会话列表
  const loadSessions = async () => {
    try {
      setSessionsLoading(true);
      const response = await conversationApi.getSessionList(marketId!, user?.user_id || '');
      if (response.success && response.data) {
        setSessions(response.data);
        // 恢复上次选中的会话，或选第一个
        if (response.data.length > 0) {
          const savedId = sessionStorage.getItem(savedSessionKey);
          const targetSession = savedId
            ? response.data.find((s: any) => s.session_id === savedId)
            : null;
          const targetId = targetSession
            ? savedId!
            : response.data[0].session_id;

          if (!currentSessionId || currentSessionId !== targetId) {
            setCurrentSessionId(targetId);
            sessionStorage.setItem(savedSessionKey, targetId);
            currentSessionIdRef.current = targetId; // 🔒 同步更新ref
          }
          await loadSessionHistory(targetId, true); // 强制加载目标会话历史
        }
      }
    } catch (error) {
      console.error('加载会话列表失败:', error);
    } finally {
      setSessionsLoading(false);
    }
  };

  // 加载指定会话的历史
  const loadSessionHistory = async (sessionId: string, forceUpdate = false) => {
    try {
      console.log('🔄 加载会话历史:', sessionId, '当前显示会话:', currentSessionId, '强制更新:', forceUpdate);
      const response = await conversationApi.getSessionHistory(sessionId);
      console.log('📦 会话历史响应:', response);
      
      let dbMessages: Message[] = [];
      if (response.success && response.data) {
        console.log('✅ 加载到', response.data.length, '条数据库消息');
        dbMessages = response.data.map((msg: any) => {
          if (msg.role === 'task_card' && msg.status === 'processing') {
            console.log(`📌 订阅处理中的任务: ${msg.task_id}`);
            subscribeTask(msg.task_id);
          }
          return msg;
        });
      } else {
        console.warn('⚠️ 会话历史为空或加载失败');
      }
      
      // 获取该会话的待处理消息
      const pendingMessages = sessionPendingMessages.get(sessionId) || [];
      console.log('📝 待处理消息数量:', pendingMessages.length);
      
      // 合并数据库消息和待处理消息
      const allMessages = [...dbMessages, ...pendingMessages];
      console.log('📋 总消息数量:', allMessages.length, '(数据库:', dbMessages.length, '+ 待处理:', pendingMessages.length, ')');
      
      // 🔒 用户主动切换会话时强制更新，API响应时条件更新
      if (forceUpdate || sessionId === currentSessionId) {
        console.log('✅ 更新当前显示的消息列表');
        setMessages(allMessages);
        // 延迟滚动到底部，确保DOM已更新
        setTimeout(() => scrollToBottom(), 100);
      } else {
        console.log('⚠️ 加载的不是当前显示的会话，不更新UI');
      }
    } catch (error) {
      console.error('❌ 加载会话历史失败:', error);
    }
  };

  // 处理URL失效（任务结果已被清理）
  const handleUrlInvalid = async (taskId: string) => {
    console.log('🗑️ 检测到任务结果URL失效:', taskId);
    
    if (!currentSessionId) {
      console.warn('⚠️ 无法更新任务状态：当前会话ID为空');
      return;
    }
    
    try {
      // 调用后端API更新任务状态为已清理
      const response = await conversationApi.updateTaskStatus(currentSessionId, taskId, 'cleaned');
      
      if (response.success) {
        console.log('✅ 任务状态已更新为已清理:', taskId);
        
        // 更新本地消息列表中的任务状态
        setMessages(prev => prev.map(msg => {
          if (msg.role === 'task_card' && msg.task_id === taskId) {
            return { ...msg, status: 'cleaned' as const };
          }
          return msg;
        }));
      } else {
        console.error('❌ 更新任务状态失败:', response.message);
      }
    } catch (error) {
      console.error('❌ 更新任务状态异常:', error);
    }
  };

  // 创建新会话
  const handleCreateSession = async () => {
    // 显示确认对话框
    Modal.confirm({
      title: '创建新对话',
      content: '确定要创建新的对话吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await conversationApi.createSession({
            user_id: user?.user_id || '',
            market_id: marketId!
          });
          if (response.success && response.data) {
            message.success('新会话已创建');
            await loadSessions();
            setCurrentSessionId(response.data.session_id);
            sessionStorage.setItem(savedSessionKey, response.data.session_id); // 持久化新会话ID
            setMessages([]);
          }
        } catch (error) {
          console.error('创建会话失败:', error);
          message.error('创建会话失败');
        }
      }
    });
  };

  // 选择会话
  const handleSelectSession = async (sessionId: string) => {
    console.log('👆 选择会话:', sessionId);
    setCurrentSessionId(sessionId);
    sessionStorage.setItem(savedSessionKey, sessionId); // 持久化选中的会话ID
    currentSessionIdRef.current = sessionId; // 🔒 立即更新ref，确保异步回调中能获取到最新值
    
    // 检查该会话是否有正在进行的loading状态
    const isSessionLoading = sessionLoadingStates.get(sessionId) || false;
    if (isSessionLoading) {
      console.log('🔄 恢复会话loading状态:', sessionId);
      setLoading(true);
      setLoadingSessionId(sessionId);
    } else {
      // 如果当前全局loading状态是针对其他会话的，清除它
      if (loadingSessionId && loadingSessionId !== sessionId) {
        setLoading(false);
        setLoadingSessionId(null);
      }
    }
    
    await loadSessionHistory(sessionId, true); // 用户主动切换会话，强制更新
  };

  // 删除会话
  const handleDeleteSession = async (sessionId: string) => {
    try {
      const response = await conversationApi.deleteSession(sessionId);
      if (response.success) {
        message.success('会话已删除');
        await loadSessions();
        // 如果删除的是当前会话，切换到第一个会话
        if (sessionId === currentSessionId) {
          const remainingSessions = sessions.filter(s => s.session_id !== sessionId);
          if (remainingSessions.length > 0) {
            handleSelectSession(remainingSessions[0].session_id);
          } else {
            setCurrentSessionId(null);
            sessionStorage.removeItem(savedSessionKey); // 无会话时清除存储
            setMessages([]);
          }
        }
      }
    } catch (error) {
      console.error('删除会话失败:', error);
      message.error('删除会话失败');
    }
  };

  // 处理图片上传（支持多张，支持模式切换）
  const handleImageUpload = async (file: File) => {
    // 检查数量限制
    if (uploadedImages.length >= maxImages) {
      message.error(`最多只能上传${maxImages}张图片`);
      return false;
    }

    // 验证文件类型
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      message.error('只支持 JPG、JPEG、PNG 格式的图片');
      return false;
    }

    // 从环境变量读取文件大小限制（MB），默认10MB
    const envSizeMB = import.meta.env.VITE_UPLOAD_IMAGE_SIZE_LIMIT;
    const maxSizeMB = envSizeMB ? parseInt(envSizeMB, 10) : 10;
    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      message.error(`图片大小不能超过 ${maxSizeMB}MB`);
      return false;
    }

    setUploading(true);
    try {
      // 使用支持模式切换的图片上传函数
      const imageUrl = await uploadImageWithMode(file);
      setUploadedImages(prev => [...prev, imageUrl]);
      message.success(`图片上传成功 (${uploadedImages.length + 1}/${maxImages})`);
    } catch (error) {
      console.error('图片上传失败:', error);
      message.error('图片上传失败');
    } finally {
      setUploading(false);
    }
    return false; // 阻止默认上传行为
  };

  // 移除指定的图片
  const handleRemoveImage = (index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    message.success('图片已移除');
  };

  // 处理拖拽上传
  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const dropTarget = e.target as HTMLElement | null;
    if (dropTarget?.closest('.ant-upload')) {
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      message.warning('请拖入图片文件');
      return;
    }

    // 检查数量限制
    const remainingSlots = maxImages - uploadedImages.length;
    if (imageFiles.length > remainingSlots) {
      message.warning(`最多还能上传${remainingSlots}张图片`);
      return;
    }

    // 依次上传所有图片
    for (const file of imageFiles) {
      await handleImageUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // 处理输入框变化，检测@符号
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // 检测是否输入了@符号
    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      
      // 关键逻辑：
      // 1. 如果@后面有空格，说明用户已经完成了mention输入，隐藏列表
      // 2. 如果@后面没有空格，显示列表并用@后面的文字过滤
      if (textAfterAt.includes(' ')) {
        // @后面有空格，隐藏列表
        setShowMentionDropdown(false);
        setMentionFilterText('');
      } else {
        // @后面没有空格，显示列表并过滤
        setMentionFilterText(textAfterAt);
        setShowMentionDropdown(true);
        setSelectedMentionIndex(0); // 重置选中索引
      }
    } else {
      // 没有@符号，隐藏列表
      setShowMentionDropdown(false);
      setMentionFilterText('');
    }
  };

  // 处理键盘事件（上下键选择，回车确认）
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showMentionDropdown || filteredMentions.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev < filteredMentions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        if (showMentionDropdown) {
          e.preventDefault();
          const selectedMention = filteredMentions[selectedMentionIndex];
          if (selectedMention) {
            handleSelectMention(selectedMention.mention_tag);
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowMentionDropdown(false);
        setMentionFilterText('');
        break;
    }
  };

  // 选择mention项
  const handleSelectMention = (mentionTag: string) => {
    const cursorPosition = inputRef.current?.resizableTextArea?.textArea?.selectionStart || 0;
    const textBeforeCursor = inputValue.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      // 获取光标后的文本
      const textAfterCursor = inputValue.substring(cursorPosition);
      
      // 构建新的输入值：
      // 1. @之前的文本
      // 2. mention tag（如 @sora）
      // 3. 一个空格
      // 4. 光标后的文本
      const newValue = inputValue.substring(0, lastAtIndex) + mentionTag + ' ' + textAfterCursor;
      setInputValue(newValue);
      
      // 隐藏下拉列表
      setShowMentionDropdown(false);
      setMentionFilterText('');
      
      // 设置光标位置到mention tag + 空格之后
      setTimeout(() => {
        const textarea = inputRef.current?.resizableTextArea?.textArea;
        if (textarea) {
          const newCursorPosition = lastAtIndex + mentionTag.length + 1; // +1 是空格
          textarea.focus();
          textarea.setSelectionRange(newCursorPosition, newCursorPosition);
        }
      }, 10);
    }
  };

  // 过滤mention列表（注意：数据库中的mention_tag已包含@符号）
  const filteredMentions = mentionList.filter(item => 
    item.mention_tag.toLowerCase().includes(mentionFilterText.toLowerCase()) ||
    item.model_config.display_name.toLowerCase().includes(mentionFilterText.toLowerCase())
  );

  // 发送消息
  const handleSend = async () => {
    if (!inputValue.trim()) {
      message.warning('请输入消息内容');
      return;
    }

    // 检查是否有活动会话，如果没有则先创建
    if (!currentSessionId) {
      Modal.confirm({
        title: '需要创建对话',
        content: '当前没有活跃的对话会话，创建新会话后发送消息？',
        okText: '创建新对话',
        cancelText: '取消',
        onOk: () => {
          return new Promise(async (resolve, reject) => {
            try {
              const response = await conversationApi.createSession({
                user_id: user?.user_id || '',
                market_id: marketId!
              });
              if (response.success && response.data) {
                message.success('新会话已创建');
                setCurrentSessionId(response.data.session_id);
                sessionStorage.setItem(savedSessionKey, response.data.session_id); // 持久化新会话ID
                currentSessionIdRef.current = response.data.session_id; // 🔒 同步更新ref，确保sendMessageWithSession中能正确匹配
                setMessages([]);
                await loadSessions();
                // 创建会话成功后，继续发送消息
                await sendMessageWithSession(response.data.session_id);
                resolve(true); // 成功时关闭对话框
              } else {
                message.error('创建会话失败');
                reject(new Error('创建会话失败'));
              }
            } catch (error) {
              console.error('创建会话失败:', error);
              message.error('创建会话失败');
              reject(error); // 失败时不关闭对话框，让用户可以重试
            }
          });
        },
        onCancel: () => {
          console.log('用户取消创建会话');
        }
      });
      return;
    }

    // 如果有活动会话，直接发送消息
    await sendMessageWithSession(currentSessionId);
  };

  // 发送消息的核心逻辑（提取为独立方法）
  const sendMessageWithSession = async (sessionId: string) => {
    // 🔒 使用ref获取最新的会话ID，避免闭包中state异步更新导致的不一致
    const requestingSessionId = sessionId;
    const currentDisplaySessionId = currentSessionIdRef.current;
    
    console.log('🚀 [DEBUG] 发送消息时的会话状态:', {
      requestingSessionId,
      currentDisplaySessionId,
      isCurrentSession: requestingSessionId === currentDisplaySessionId
    });
    
    const userMessage: Message = {
      role: 'user',
      content: inputValue,
      timestamp: new Date().toISOString(),
      image_urls: uploadedImages.length > 0 ? uploadedImages : undefined
    };

    // 🔒 将用户消息添加到该会话的待处理消息缓存中
    setSessionPendingMessages(prev => {
      const newMap = new Map(prev);
      const existingMessages = newMap.get(requestingSessionId) || [];
      newMap.set(requestingSessionId, [...existingMessages, userMessage]);
      return newMap;
    });
    
    // 🔒 只有用户当前就在发起请求的会话中时，才立即显示用户消息
    if (requestingSessionId === currentDisplaySessionId) {
      console.log('✅ 用户在当前会话发送消息，立即显示');
      setMessages(prev => [...prev, userMessage]);
    } else {
      console.log('⚠️ 用户切换到其他会话后发送消息，不显示在当前会话');
    }
    
    const messageToSend = inputValue;
    const imagesToSend = [...uploadedImages]; // 复制数组
    
    setInputValue('');
    setUploadedImages([]); // 清空所有图片
    setShowMentionDropdown(false); // 隐藏mention下拉列表
    setMentionFilterText(''); // 清空过滤文本
    setLoading(true);
    setLoadingSessionId(requestingSessionId); // 设置当前加载的会话ID
    
    // 在Map中记录该会话的loading状态
    setSessionLoadingStates(prev => {
      const newMap = new Map(prev);
      newMap.set(requestingSessionId, true);
      return newMap;
    });

    // 如果有图片，显示提示信息
    if (imagesToSend.length > 0) {
      message.info('图片分析中，请耐心等待...', 3);
    }

    try {
      console.log('📤 发送消息:', {
        market_id: marketId,
        user_id: user?.user_id,
        message: messageToSend.substring(0, 50),
        session_id: sessionId,
        images_count: imagesToSend.length
      });
      
      const response = await conversationApi.sendMessage({
        market_id: marketId!,
        user_id: user?.user_id || '',
        message: messageToSend,
        session_id: sessionId,
        image_urls: imagesToSend.length > 0 ? imagesToSend : undefined // 发送图片数组
      });
      
      console.log('📥 发送消息响应:', response);
      console.log('🔍 响应数据详情:', {
        success: response.success,
        data: response.data,
        is_async: response.data?.is_async,
        task_id: response.data?.task_id,
        reply: response.data?.reply
      });

      if (response.success && response.data) {
        // 检查是否为异步任务
        if (response.data.is_async && response.data.task_id) {
          console.log('🔄 异步任务已启动:', response.data.task_id);
          
          // 添加任务卡片消息
          const taskCardMessage: Message = {
            role: 'task_card',
            task_id: response.data.task_id,
            task_name: response.data.reply.replace('任务已启动：', ''),
            status: 'processing',
            timestamp: new Date().toISOString(),
            created_at: new Date().toISOString()
          };
          
          // 🔒 统一使用缓存机制，不再直接操作messages状态
          console.log('📝 [DEBUG] 将异步任务响应添加到缓存:', {
            requestingSessionId,
            taskId: response.data.task_id
          });
          
          // 将任务卡片添加到发起请求会话的待处理消息缓存
          setSessionPendingMessages(prev => {
            const newMap = new Map(prev);
            const existingMessages = newMap.get(requestingSessionId) || [];
            newMap.set(requestingSessionId, [...existingMessages, taskCardMessage]);
            console.log('📝 任务缓存更新后的消息数量:', [...existingMessages, taskCardMessage].length);
            return newMap;
          });
          
          // 🔒 使用ref获取最新的会话ID，避免异步状态问题
          if (currentSessionIdRef.current === requestingSessionId) {
            console.log('🔄 当前显示的是发起请求的会话，刷新消息显示');
            await loadSessionHistory(requestingSessionId);
          } else {
            console.log('⚠️ 当前显示的不是发起请求的会话，不刷新UI', {
              current: currentSessionIdRef.current,
              requesting: requestingSessionId
            });
          }
          
          // 启动任务状态监控
          subscribeTask(response.data.task_id);
        } else {
          // 同步响应，添加普通助手消息
          const assistantMessage: Message = {
            role: 'assistant',
            content: response.data.reply,
            timestamp: new Date().toISOString()
          };
          
          // 🔒 统一使用缓存机制，不再直接操作messages状态
          console.log('📝 [DEBUG] 将同步响应添加到缓存:', {
            requestingSessionId
          });
          
          // 将助手消息添加到发起请求会话的待处理消息缓存
          setSessionPendingMessages(prev => {
            const newMap = new Map(prev);
            const existingMessages = newMap.get(requestingSessionId) || [];
            newMap.set(requestingSessionId, [...existingMessages, assistantMessage]);
            console.log('📝 同步响应缓存更新后的消息数量:', [...existingMessages, assistantMessage].length);
            return newMap;
          });
          
          // 🔒 使用ref获取最新的会话ID，避免异步状态问题
          if (currentSessionIdRef.current === requestingSessionId) {
            console.log('🔄 当前显示的是发起请求的会话，刷新消息显示');
            await loadSessionHistory(requestingSessionId);
          } else {
            console.log('⚠️ 当前显示的不是发起请求的会话，不刷新UI', {
              current: currentSessionIdRef.current,
              requesting: requestingSessionId
            });
          }
        }
        
        // 显示使用的模型信息
        if (response.data.model_used) {
          console.log('🤖 使用的模型:', response.data.model_used);
        }
        
        // 刷新会话列表（可能标题已更新）
        await loadSessions();
        
        // ✅ API成功时，消息已经通过缓存机制处理，现在清除缓存让数据库成为唯一数据源
        console.log('✅ API成功，清除缓存，让数据库成为数据源');
        setSessionPendingMessages(prev => {
          const newMap = new Map(prev);
          newMap.delete(requestingSessionId);
          return newMap;
        });
      } else {
        message.error(response.message || '发送消息失败');
        // API失败时清除用户消息缓存
        setSessionPendingMessages(prev => {
          const newMap = new Map(prev);
          newMap.delete(requestingSessionId);
          return newMap;
        });
      }
    } catch (error: any) {
      console.error('发送消息失败:', error);
      message.error(error.message || '发送消息失败');
      // 异常时清除用户消息缓存
      setSessionPendingMessages(prev => {
        const newMap = new Map(prev);
        newMap.delete(requestingSessionId);
        return newMap;
      });
    } finally {
      setLoading(false);
      setLoadingSessionId(null); // 清除加载会话ID
      
      // 从Map中清除该会话的loading状态
      setSessionLoadingStates(prev => {
        const newMap = new Map(prev);
        newMap.delete(requestingSessionId);
        return newMap;
      });
    }
  };

  // 处理Enter键发送
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };



  // 获取用户头像（用户名首字母大写）
  const getUserAvatar = () => {
    if (user?.user_name) {
      return user.user_name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // 打开/关闭组件配置面板
  const handleToggleComponentPanel = () => {
    setComponentPanelVisible(!componentPanelVisible);
  };

  // 使用说明弹窗处理函数
  const showInstructionsModal = () => {
    if (!instructionsContent) {
      message.info('暂无使用说明');
      return;
    }
    setIsInstructionsModalVisible(true);
  };

  const handleInstructionsModalClose = () => {
    // 停止所有正在播放的视频和音频
    document.querySelectorAll<HTMLVideoElement | HTMLAudioElement>('video, audio').forEach(el => {
      el.pause();
    });
    setIsInstructionsModalVisible(false);
  };

  // 文件上传处理（延迟上传，只保存文件引用）
  const handleFileChange = async (variable: string, file: File | null) => {
    if (file) {
      console.log(`📁 文件选择: ${variable} -> ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
      const previewToken = `${file.name}_${file.size}_${file.lastModified}_${Date.now()}`;
      uploadedFilePreviewTokensRef.current[variable] = previewToken;

      setUploadedFiles(prev => ({
        ...prev,
        [variable]: file
      }));

      updateUploadedFilePreview(variable, null);

      let previewUrl: string | null = null;

      if (isImageFile(file)) {
        previewUrl = URL.createObjectURL(file);
      } else if (isVideoFile(file)) {
        previewUrl = await generateVideoThumbnail(file);
      }

      if (uploadedFilePreviewTokensRef.current[variable] === previewToken) {
        updateUploadedFilePreview(variable, previewUrl);
      } else {
        revokePreviewUrl(previewUrl);
      }
    } else {
      console.log(`🗑️ 移除文件: ${variable}`);
      delete uploadedFilePreviewTokensRef.current[variable];
      updateUploadedFilePreview(variable, null);
      setUploadedFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[variable];
        return newFiles;
      });
      form.setFieldsValue({ [variable]: [] });
    }
  };

  // 检查云服务器状态
  const checkCloudServerStatus = async (): Promise<boolean> => {
    try {
      console.log('🔍 开始检查云服务器状态，用户ID:', user?.user_id);
      // 不需要传递 user_id 参数，后端会从 JWT token 中获取
      const response = await api.get('/cloud-servers') as ApiResponse<any[]>;
      console.log('📡 云服务器列表响应:', response);
      
      if (response.success && response.data) {
        console.log('✅ 获取到服务器列表，总数:', response.data.length);
        console.log('📋 服务器详情:', response.data);
        
        // 添加详细的字段类型日志
        // if (response.data.length > 0) {
        //   const firstServer = response.data[0];
        //   console.log('🔍 第一个服务器的字段类型:', {
        //     srv_status: firstServer.srv_status,
        //     srv_status_type: typeof firstServer.srv_status,
        //     isdisplay: firstServer.isdisplay,
        //     isdisplay_type: typeof firstServer.isdisplay
        //   });
        // }
        
        const runningServers = response.data.filter(
          (server: any) => {
            const isRunning = server.srv_status === 'running';
            const isDisplay = server.isdisplay == 1; // 使用宽松比较，兼容数字和字符串
            console.log(`🔍 服务器 ${server.srv_name || server.cloud_srv_id}: running=${isRunning}, display=${isDisplay} (isdisplay值=${server.isdisplay}, 类型=${typeof server.isdisplay})`);
            return isRunning && isDisplay;
          }
        );
        
        console.log('🟢 运行中的服务器数量:', runningServers.length);

        // 进一步检查是否有 ComfyUI 连接成功的服务器
        const connectedServers = runningServers.filter(
          (server: any) => {
            const isConnected = server.comfyui_status === 'connected';
            console.log('🔍 服务器 ' + (server.srv_name || server.cloud_srv_id) + ': comfyui_status=' + server.comfyui_status + ', connected=' + isConnected);
            return isConnected;
          }
        );

        console.log('🟢 ComfyUI已连接的服务器数量:', connectedServers.length);
        if (connectedServers.length > 0) {
          console.log('✅ 找到ComfyUI已连接的服务器:', connectedServers);
        } else {
          console.warn('⚠️  没有找到ComfyUI已连接的服务器');
          console.log('所有服务器状态:', response.data.map((s: any) => ({
            name: s.srv_name,
            srv_status: s.srv_status,
            comfyui_status: s.comfyui_status,
            isdisplay: s.isdisplay
          })));
        }

        return connectedServers.length > 0;
      }
      
      console.warn('⚠️  API 响应不成功或无数据:', response);
      return false;
    } catch (error: any) {
      console.error('❌ 检查云服务器状态失败:', error);
      console.error('错误详情:', error.response?.data || error.message);
      return false;
    }
  };

  // 上传文件（支持TOS和云服务器两种模式）
  // compressImage: 是否在上传前压缩图片（仅对话左侧上传图片时为 true）
  const uploadFilePlus = async (file: File, compressImage = false, maxSizeMB?: number): Promise<string> => {
    const { getUploadConfig, validateFileSize, getFileSizeErrorMessage, getUploadModeDisplayName, logUploadConfig } = await import('../utils/uploadConfig');
    
    // 打印配置信息
    logUploadConfig();
    
    const config = getUploadConfig();
    
    // 检查文件大小
    if (!validateFileSize(file, maxSizeMB)) {
      throw new Error(getFileSizeErrorMessage(file.name, maxSizeMB));
    }

    console.log(`📤 文件上传模式: ${config.mode} (${getUploadModeDisplayName(config.mode)})`);

    if (config.mode === 'server') {
      // 上传到云服务器
      const result = await uploadFileToServer(file, false, compressImage);
      return Array.isArray(result) ? result[0] : result;
    } else {
      // 上传到TOS云存储（默认）
      return await uploadFileToTOSCloud(file, maxSizeMB);
    }
  };

  // 上传文件到TOS云存储
  const uploadFileToTOSCloud = async (file: File, maxSizeMB?: number): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    if (maxSizeMB && Number.isFinite(maxSizeMB)) {
      formData.append('maxSizeMB', String(maxSizeMB));
    }
    
    console.log('📤 上传到TOS云存储...');
    const response = await api.post('/upload/file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }) as ApiResponse<{ url: string }>;
    
    if (response.success) {
      console.log('✅ TOS上传成功:', response.data!.url);
      return response.data!.url;
    } else {
      throw new Error(response.message || 'TOS文件上传失败');
    }
  };

  // 图片压缩函数：将图片压缩到目标大小以内
  const compressImageForUpload = (file: File, targetSizeKB = 500): Promise<File> => {
    return new Promise((resolve, reject) => {
      // 非图片文件或已经小于目标大小，直接返回
      if (!file.type.startsWith('image/') || file.size <= targetSizeKB * 1024) {
        resolve(file);
        return;
      }

      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;

          // 限制最大尺寸为 1920px
          const maxDimension = 1920;
          if (width > maxDimension || height > maxDimension) {
            const ratio = Math.min(maxDimension / width, maxDimension / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) { resolve(file); return; }
          ctx.drawImage(img, 0, 0, width, height);

          // 逐步降低质量直到满足目标大小
          const tryCompress = (quality: number) => {
            canvas.toBlob(
              (blob) => {
                if (!blob) { resolve(file); return; }
                if (blob.size <= targetSizeKB * 1024 || quality <= 0.3) {
                  const compressedFile = new File([blob], file.name, {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                  });
                  console.log(`🖼️ 图片压缩: ${(file.size / 1024).toFixed(1)}KB -> ${(compressedFile.size / 1024).toFixed(1)}KB (quality=${quality.toFixed(2)}, ${width}x${height})`);
                  resolve(compressedFile);
                } else {
                  tryCompress(quality - 0.1);
                }
              },
              'image/jpeg',
              quality
            );
          };
          tryCompress(0.85);
        };
        img.onerror = () => resolve(file);
        img.src = e.target?.result as string;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  // 带超时和重试的 fetch 请求
  const fetchWithRetry = async (url: string, options: RequestInit, maxRetries = 6, timeoutMs = 30000): Promise<Response> => {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        lastError = error instanceof Error ? error : new Error(String(error));
        const isNetworkError = lastError.message === 'Failed to fetch' ||
          lastError.name === 'AbortError' ||
          lastError.message.includes('ERR_CONNECTION_RESET') ||
          lastError.message.includes('ERR_NETWORK');

        if (!isNetworkError || attempt === maxRetries) {
          throw lastError;
        }

        const retryDelay = attempt * 2000; // 2s, 4s, 6s
        console.warn(`🔄 上传重试 (${attempt}/${maxRetries}): ${retryDelay}ms 后重试, 错误: ${lastError.message}`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
    throw lastError || new Error('上传失败');
  };

  // 上传文件到云服务器（支持批量压缩文件处理）
  // compressBeforeUpload: 仅对话左侧上传图片时为 true，创建任务中的图片不压缩
  const uploadFileToServer = async (file: File, isBatchFile = false, compressBeforeUpload = false): Promise<string | string[]> => {
    const { getUploadConfig } = await import('../utils/uploadConfig');
    const config = getUploadConfig();
    
    // 仅在对话图片上传时压缩，创建任务的图片按原图上传
    const fileToUpload = (compressBeforeUpload && file.type.startsWith('image/')) ? await compressImageForUpload(file, 500) : file;
    
    // 生成唯一的文件名：原文件名_时间戳_随机数
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 8);
    const fileExtension = fileToUpload.type === 'image/jpeg' ? 'jpg' : (file.name.split('.').pop()?.toLowerCase() || '');
    const originalNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
    const uniqueFileName = `${originalNameWithoutExt}_${timestamp}_${randomId}.${fileExtension}`;
    
    console.log(`📋 文件信息: ${file.name} -> ${uniqueFileName} (原始: ${(file.size / 1024 / 1024).toFixed(2)}MB, 压缩后: ${(fileToUpload.size / 1024 / 1024).toFixed(2)}MB)`);
    
    // 创建一个新的File对象，使用唯一的文件名
    const renamedFile = new File([fileToUpload], uniqueFileName, { type: fileToUpload.type });
    
    const formData = new FormData();
    formData.append('file', renamedFile);
    
    console.log(`📤 上传到云服务器: ${config.serverTempUploadUrl}`);
    
    try {
      const response = await fetchWithRetry(config.serverTempUploadUrl, {
        method: 'POST',
        body: formData,
        mode: 'cors',
        headers: {
          'Authorization': `Bearer ${config.apiToken}`
        }
      }, 10, 30000);
      
      console.log('云服务器响应状态:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ 云服务器响应错误:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('📦 云服务器响应数据:', result);
      
      if (result.status === 'success') {
        console.log('✅ 云服务器上传成功:', {
          filename: result.filename,
          url: result.url,
          path: result.path,
          upload_type: result.upload_type
        });
        
        // 如果是批量文件（压缩包），需要解压处理
        if (isBatchFile && ['zip', 'rar', '7z'].includes(fileExtension)) {
          console.log('🗜️ 检测到压缩文件，开始解压处理...');
          return await processBatchFile(result.path, result.filename);
        }
        
        // 使用 resourceBaseUrl 生成缩略图访问 URL（而非 serverBaseUrl）
        // serverBaseUrl 仅用于 API 调用，resourceBaseUrl 用于静态资源访问
        let fullUrl: string;
        if (result.url) {
          // 服务器返回的 URL 可能使用了 serverBaseUrl，需要替换为 resourceBaseUrl
          const urlPath = result.url.replace(/^https?:\/\/[^\/]+/, '');
          fullUrl = `${config.resourceBaseUrl}${urlPath}`;
          console.log('🔗 使用 resourceBaseUrl 替换服务器返回的URL:', fullUrl);
        } else {
          // 降级：使用 resourceBaseUrl 拼接
          fullUrl = `${config.resourceBaseUrl}/temp/${result.filename}`;
          console.log('🔗 使用 resourceBaseUrl 生成URL:', fullUrl);
        }
        return fullUrl;
      } else {
        throw new Error(result.message || '云服务器上传失败');
      }
    } catch (error) {
      console.error('❌ 云服务器上传失败:', error);
      console.error('错误详情:', {
        message: error instanceof Error ? error.message : '未知错误',
        fileName: uniqueFileName,
        fileSize: `${(fileToUpload.size / 1024 / 1024).toFixed(2)}MB`,
        uploadUrl: config.serverTempUploadUrl
      });
      throw new Error(`云服务器上传失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  };

  // 统一的图片上传逻辑（对话左侧上传图片专用，启用压缩）
  const uploadImageWithMode = async (file: File): Promise<string> => {
    console.log('🖼️ 开始对话图片上传（启用压缩）...');
    // uploadFilePlus 已经包含了 TOS 和云服务器的模式选择逻辑，compressImage=true 启用压缩
    return await uploadFilePlus(file, true);
  };

  // 处理批量压缩文件
  const processBatchFile = async (filePath: string, fileName: string): Promise<string[]> => {
    try {
      console.log('📦 开始处理批量压缩文件:', fileName);
      
      // 调用后端API解压文件
      const response = await api.post('/upload/extract-batch', {
        filePath,
        fileName
      }) as ApiResponse<{ imageFiles: string[], textFiles: string[] }>;
      
      if (response.success && response.data) {
        const { imageFiles, textFiles } = response.data;
        console.log('✅ 压缩文件解压成功:', {
          imageFiles: imageFiles.length,
          textFiles: textFiles.length
        });
        
        // 使用配置的服务器基础地址生成文件 URL
        const { getUploadConfig } = await import('../utils/uploadConfig');
        const config = getUploadConfig();
        
        const allFiles = [...imageFiles, ...textFiles].map(file => 
          `${config.serverBaseUrl}/temp/${file}`
        );
        
        return allFiles;
      } else {
        throw new Error(response.message || '解压文件失败');
      }
    } catch (error) {
      console.error('❌ 处理批量文件失败:', error);
      throw error;
    }
  };

  // 处理创建任务
  const handleCreateTask = async () => {
    // 积分扣费已移入工作流 API 内部处理，前端不再需要追踪临时任务ID
    // let taskIdForPoints: string | null = null;
    
    try {
      console.log('🔍 [SUBMIT] 开始创建任务...');
      console.log('🔍 [SUBMIT] 当前会话ID:', currentSessionId);
      
      // 0. 检查是否在会话中
      if (!currentSessionId) {
        console.warn('⚠️  没有当前会话，无法创建任务');
        Modal.confirm({
          title: '需要创建对话',
          content: '当前没有活跃的对话会话。创建任务需要在对话会话中进行，是否立即创建新的对话？',
          okText: '创建新对话',
          cancelText: '取消',
          onOk: async () => {
            console.log('🔄 用户选择创建新对话');
            try {
              await handleCreateSession();
              // 创建成功后，可以提示用户再次尝试创建任务
              // message.success('新对话已创建，现在可以创建任务了');
            } catch (error) {
              console.error('❌ 创建新对话失败:', error);
              message.error('创建新对话失败，请手动创建');
            }
          },
          onCancel: () => {
            console.log('用户取消创建对话');
          }
        });
        return;
      }
      
      console.log('🔍 [SUBMIT] 开始表单验证...');
      console.log('🔍 [SUBMIT] 当前表单配置:', workflowInfo?.component_template);
      console.log('🔍 [SUBMIT] 当前激活的 Tab:', activeStepKey);
      
      // 1. 获取当前 Tab 的字段配置
      const activeStepFields = getCurrentStepFields();
      const activeFieldNames = activeStepFields.flatMap(getComponentFormFieldNames);
      console.log('🔍 [SUBMIT] 当前 Tab 字段:', activeFieldNames);
      
      // 1.1 只验证当前 Tab 的字段
      const values = await form.validateFields(activeFieldNames);
      console.log('✅ [SUBMIT] 表单验证通过，值:', values);
      
      // 2. 检查模型类型和资源
      if (user?.user_type === 'trial' && workflowInfo?.access_type === 'pro') {
        Modal.warning({
          title: '需要升级专业用户',
          content: '该工作流仅限专业用户使用，请先升级到专业用户后再创建任务。',
          okText: '知道了'
        });
        return;
      }

      if (workflowInfo?.model_type === 'closedsource') {
        // 闭源模型：检查积分
        if (!selectedModelId) {
          message.error('请选择模型渠道');
          return;
        }
        
        const selectedModel = availableModels.find(m => m.model_id === selectedModelId);
        if (!selectedModel) {
          message.error('选择的模型渠道无效');
          return;
        }
        
        const requiredPoints = selectedModel.cost_points || 0;
        if (userPoints < requiredPoints) {
          Modal.warning({
            title: '积分不足',
            content: (
              <div>
                <p>当前选择的模型渠道需要 <strong>{requiredPoints}</strong> 积分</p>
                <p>您当前剩余积分：<strong>{userPoints}</strong></p>
                <p>请充值积分或选择其他模型渠道</p>
              </div>
            ),
            okText: '知道了'
          });
          return;
        }
        
        console.log(`✅ 积分检查通过：需要 ${requiredPoints} 积分，剩余 ${userPoints} 积分`);
      } else if (workflowInfo?.model_type === 'apisource') {
        // API 模型：检查是否选择了渠道分组
        if (!selectedGroupId) {
          message.error('请选择模型渠道');
          return;
        }
        const selectedGroup = callGroups.find((g: CallGroup) => g.group_id === selectedGroupId);
        if (!selectedGroup) {
          message.error('选择的模型渠道无效');
          return;
        }
        // 如果有预扣积分，检查用户余额
        const preDeductedPoints = getCallGroupPreDeductedPoints(selectedGroup);
        if (preDeductedPoints > 0 && userPoints < preDeductedPoints) {
          Modal.warning({
            title: '积分不足',
            content: (
              <div>
                <p>当前渠道预扣积分：<strong>{preDeductedPoints}</strong> 积分</p>
                <p>您当前剩余积分：<strong>{userPoints}</strong></p>
                <p>请充值积分或选择其他渠道</p>
              </div>
            ),
            okText: '知道了'
          });
          return;
        }
        console.log(`✅ apisource 渠道检查通过: ${selectedGroup.group_name}`);
      } else {
        // 开源模型：需要至少一台运行中且 ComfyUI 工作流已连接的云服务器
        const hasConnectedWorkflowServer = await checkCloudServerStatus();
        if (!hasConnectedWorkflowServer) {
          message.error('当前云服务器未开启或未成功连接工作流，无法创建任务。请先启动云服务器并确认工作流连接成功。');
          return;
        }
      }
      
      // 3. 检查试用用户权限
      console.log('🔍 检查试用用户权限...');
      const permissionResponse = await authApi.checkTaskPermission();
      console.log('📊 权限检查结果:', permissionResponse);
      
      if (permissionResponse.success && permissionResponse.data) {
        const { canExecute, message: permissionMessage, remainingTimes } = permissionResponse.data;
        
        if (!canExecute) {
          console.warn('⚠️  试用用户权限不足:', permissionMessage);
          Modal.warning({
            title: '权限不足',
            content: permissionMessage,
            okText: '知道了'
          });
          return;
        }
        
        if (user?.user_type === 'trial' && remainingTimes >= 0) {
          console.log(`✅ 权限检查通过，剩余次数: ${remainingTimes}`);
        }
      }
      
      // 4. 准备参数数据（不上传文件，只准备表单数据）
      const paramData: Record<string, string> = {
        ...values
      };
      
      console.log('📦 参数数据:', paramData);
      
      // 5.1. 创建文件名映射和预览URL映射（用于显示）
      const fileNameMap: Record<string, string> = {};
      const filePreviewUrlMap: Record<string, string> = {};
      for (const [variable, file] of Object.entries(uploadedFiles)) {
        const f = file as File;
        fileNameMap[variable] = f.name;
        filePreviewUrlMap[variable] = URL.createObjectURL(f);
      }
      
      // 5.2. 创建文件类型参数集合（用于判断哪些参数是文件类型）
      // component 字段可能是 'file' 或 'upload'，两种都需要识别
      const currentFields = getCurrentStepFields();
      const fileTypeVariables = new Set<string>();
      currentFields.forEach((config: ComponentConfig) => {
        getComponentFileFieldNames(config).forEach((variable) => fileTypeVariables.add(variable));
      });
      
      // 5.5. 创建变量名到中文名称的映射
      const variableNameMap: Record<string, string> = {};
      currentFields.forEach((config: ComponentConfig) => {
        if (isVideoCoordinateComponent(config)) {
          const { uploadVariable, coordinateVariable } = getVideoCoordinateVariables(config);
          if (uploadVariable) {
            variableNameMap[uploadVariable] = config.name;
          }
          if (coordinateVariable) {
            variableNameMap[coordinateVariable] = config.coordinate_label || '字幕区域坐标';
          }
          return;
        }

        getComponentFormFieldNames(config).forEach((variable) => {
          variableNameMap[variable] = config.name;
        });
      });
      
      // 6. 显示确认对话框
      const confirmed = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: '确认创建任务',
          content: (
            <div>
              <p>请确认以下参数：</p>
              <div style={{ 
                background: '#f5f5f5', 
                padding: '16px', 
                borderRadius: '4px',
                maxHeight: '400px',
                overflow: 'auto'
              }}>
                {currentFields.flatMap((config: ComponentConfig) => getComponentFormFieldNames(config).map((key) => {
                  const value = paramData[key];
                  const displayName = variableNameMap[key] || config.name || key;
                  
                  return (
                    <div key={key} style={{ marginBottom: '12px' }}>
                      <div style={{ fontWeight: 'bold', color: '#1890ff', marginBottom: '4px' }}>
                        {displayName}:
                      </div>
                      <div style={{ 
                        paddingLeft: '12px', 
                        color: '#666',
                        wordBreak: 'break-all'
                      }}>
                        {renderConfirmParamValue(key, value, fileTypeVariables, fileNameMap, filePreviewUrlMap)}
                      </div>
                    </div>
                  );
                }))}
              </div>
            </div>
          ),
          okText: '确定创建',
          cancelText: '取消',
          width: 600,
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        });
      });
      
      if (!confirmed) {
        return;
      }
      
      setSubmitting(true);
      
      // 5. 先上传文件（确认创建后才上传）
      console.log('📤 开始上传文件...');
      
      // 显示上传进度提示
      const uploadFileCount = Object.keys(uploadedFiles).length;
      if (uploadFileCount > 0) {
        message.loading({ 
          content: `正在上传文件，请稍候...（共${uploadFileCount}个文件）`, 
          key: 'upload-progress',
          duration: 0 // 不自动消失
        });
      }
      
      const uploadedUrls: Record<string, string | string[]> = {};
      const batchProcessingData: any[] = [];
      
      // 只处理当前 Tab 的字段对应的文件
      const uploadFieldNames = new Set(activeStepFields.flatMap(getComponentFileFieldNames));
      
      for (const [variable, file] of Object.entries(uploadedFiles)) {
        // 跳过不属于当前 Tab 的文件
        if (!uploadFieldNames.has(variable)) {
          console.log(`⏭️ 跳过非当前 Tab 的文件: ${variable}`);
          continue;
        }
        
        try {
          // 更新当前上传文件的进度
          message.loading({ 
            content: `正在上传 ${variable}...`, 
            key: 'upload-progress',
            duration: 0
          });
          
          // 检查是否是批量处理文件（使用当前步骤的字段配置）
          const componentConfig = activeStepFields.find((config: ComponentConfig) => getComponentFileFieldNames(config).includes(variable));
          const isBatchFile = componentConfig?.batch_process === true;
          
          console.log(`📁 处理文件: ${variable}, 批量处理: ${isBatchFile}`);
          
          if (isBatchFile) {
            // 批量文件处理
            const fileUrls = await uploadFileToServer(file as File, true) as string[];
            console.log(`🗂️ 批量文件解压结果: ${fileUrls.length} 个文件`);
            
            // 分离图片和文本文件
            const imageFiles = fileUrls.filter(url => /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url));
            const textFiles = fileUrls.filter(url => /\.txt$/i.test(url));
            
            console.log(`📊 文件分类: 图片 ${imageFiles.length} 个, 文本 ${textFiles.length} 个`);
            
            // 按文件名排序
            imageFiles.sort();
            textFiles.sort();
            
            // 生成批量任务数据
            const minLength = Math.min(imageFiles.length, textFiles.length);
            for (let i = 0; i < minLength; i++) {
              batchProcessingData.push({
                batch_image_url: imageFiles[i],
                batch_txt_url: textFiles[i],
                index: i + 1
              });
            }
            
            console.log(`🔄 生成 ${batchProcessingData.length} 个批量任务`);
            
          } else {
            // 普通文件处理
            const { getUploadConfig } = await import('../utils/uploadConfig');
            const config = getUploadConfig();
            
            if (config.mode === 'server') {
              const url = await uploadFileToServer(file as File, false) as string;
              uploadedUrls[variable] = url;
            } else {
              const url = await uploadFilePlus(file as File, false, componentConfig?.file_size);
              uploadedUrls[variable] = url;
            }
          }
          
          message.success({ content: `${variable} 上传成功`, key: `upload-${variable}`, duration: 2 });
        } catch (error: any) {
          message.error({ content: `${variable} 上传失败: ${error.message}`, key: 'upload-progress' });
          throw error;
        }
      }
      
      // 关闭上传进度提示
      message.destroy('upload-progress');
      if (uploadFileCount > 0) {
        message.success(`所有文件上传完成！（共${uploadFileCount}个文件）`, 2);
      }
      
      // 更新参数数据
      Object.assign(paramData, uploadedUrls);
      console.log('📦 最终参数数据:', paramData);
      console.log('🔄 批量处理数据:', batchProcessingData);
      
      // 构建 display_params：用于对话记录中展示，文件类型字段只保留文件名
      const display_params: Record<string, string> = {};
      currentFields.forEach((config: ComponentConfig) => {
        getComponentFormFieldNames(config).forEach((key) => {
          const displayName = variableNameMap[key] || config.name || key;
          if (fileTypeVariables.has(key)) {
            // 文件类型：只保留文件名（不保留 URL）
            const fileName = fileNameMap[key];
            if (fileName) {
              display_params[displayName] = fileName;
            }
          } else if (paramData[key] !== undefined && paramData[key] !== '') {
            // 非文件类型：直接保留值
            display_params[displayName] = String(paramData[key]);
          }
        });
      });
      
      // 7. 如果没有当前会话，先创建一个
      if (!currentSessionId) {
        console.log('🆕 没有当前会话，先创建新会话');
        const sessionResponse = await conversationApi.createSession({
          user_id: user?.user_id || '',
          market_id: marketId!
        });
        if (sessionResponse.success && sessionResponse.data) {
          setCurrentSessionId(sessionResponse.data.session_id);
          sessionStorage.setItem(savedSessionKey, sessionResponse.data.session_id); // 持久化新会话ID
          await loadSessions();
          console.log('✅ 新会话已创建:', sessionResponse.data.session_id);
        }
      }
      
      // 7.5. 闭源模型积分扣除 - 已移入工作流 API 内部处理，前端不再单独扣费
      // 积分扣除与返还现在由 db_operate_api.py 中的 /api/v1/wallet/consume 和 /api/v1/wallet/refund 接口
      // 在工作流执行过程中自动处理，确保积分操作与 API 调用绑定在一起
      // if (workflowInfo?.model_type === 'closedsource') {
      //   const selectedModel = availableModels.find(m => m.model_id === selectedModelId);
      //   if (selectedModel) {
      //     const requiredPoints = selectedModel.cost_points || 0;
      //     if (requiredPoints > 0) {
      //       // 积分扣除逻辑已移至工作流 API 内部
      //     }
      //   }
      // }
      
      // 8. 调用后端接口创建 Coze 任务
      let responses: any[] = [];
      
      if (batchProcessingData.length > 0) {
        // 批量任务创建（限制数量）
        const batchLimit = parseInt(import.meta.env.VITE_BATCH_PROCESS_LIMIT) || 10;
        const limitedBatchData = batchProcessingData.slice(0, batchLimit);
        
        if (batchProcessingData.length > batchLimit) {
          console.warn(`⚠️ 批量任务数量超过限制，只处理前 ${batchLimit} 个任务`);
          message.warning(`批量任务数量超过限制（${batchLimit}个），只处理前${batchLimit}个任务`);
        }
        
        console.log(`🔄 开始创建 ${limitedBatchData.length} 个批量任务（限制：${batchLimit}）`);
        
        // 获取当前 Tab 对应的 workflow_id（多步骤工作流时使用）
        const batchWfId = getCurrentStepWorkflowId();
        
        for (const batchData of limitedBatchData) {
          const batchRequestData = {
            market_id: marketId,
            user_id: user?.user_id,
            param_data: {
              ...paramData,
              ...batchData
            },
            session_id: currentSessionId,
            is_batch: true,
            batch_index: batchData.index,
            display_params,
            // 多步骤工作流时传递当前步骤的 wf_id
            ...(batchWfId ? { wf_id: batchWfId } : {}),
            // 闭源模型时传递 model_id
            ...(workflowInfo?.model_type === 'closedsource' && selectedModelId ? { model_id: selectedModelId } : {})
          };
          
          console.log(`📤 发送批量任务创建请求 ${batchData.index}:`, batchRequestData);
          
          const batchResponse = await api.post('/coze-tasks/create', batchRequestData) as ApiResponse<any>;
          responses.push(batchResponse);
          
          // 添加小延迟避免请求过快
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } else if (workflowInfo?.model_type === 'apisource') {
        // apisource：后端同步调用耗时可达数分钟，不能 await 阻塞主流程
        // 后端已改为：先保存 processing 状态任务卡到数据库，再调用 API，最后更新状态
        // 前端策略：先本地插入 processing 任务卡，再等待后端最终结果
        const requestingSessionId = currentSessionIdRef.current || currentSessionId;
        const pendingTimestamp = new Date().toISOString();
        const pendingTaskId = `pending_api_${Date.now()}`;
        const pendingTaskCard: Message = {
          role: 'task_card',
          task_id: pendingTaskId,
          task_name: `${workflowInfo.display_name}_${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}`,
          status: 'processing',
          created_at: pendingTimestamp,
          timestamp: pendingTimestamp,
          params: Object.keys(display_params).length > 0 ? display_params : null
        };

        setSessionPendingMessages(prev => {
          const newMap = new Map(prev);
          const existingMessages = newMap.get(requestingSessionId) || [];
          newMap.set(requestingSessionId, [...existingMessages, pendingTaskCard]);
          return newMap;
        });

        if (currentSessionIdRef.current === requestingSessionId) {
          setMessages(prev => [...prev, pendingTaskCard]);
        }

        setComponentPanelVisible(false);
        form.resetFields();
        clearUploadedWorkflowFiles();
        message.success('任务已提交，正在生成中...');

        // 后台异步等待后端结果
        // 获取所选模型渠道的名称，用于积分流水显示
        const selectedGroup = callGroups.find(g => g.group_id === selectedGroupId);
        const apiRequestData = {
          market_id: marketId!,
          user_id: user?.user_id || '',
          param_data: paramData,
          session_id: requestingSessionId,
          group_id: selectedGroupId!,
          group_name: selectedGroup?.group_name || '',
          display_params
        };
        console.log('📤 发送 apisource 任务创建请求 (后台异步):', apiRequestData);
        apiTaskApi.createApiTask(apiRequestData).then(async (apiResponse) => {
          setSessionPendingMessages(prev => {
            const newMap = new Map(prev);
            const existingMessages = newMap.get(requestingSessionId) || [];
            const filteredMessages = existingMessages.filter(msg => msg.task_id !== pendingTaskId);
            if (filteredMessages.length > 0) {
              newMap.set(requestingSessionId, filteredMessages);
            } else {
              newMap.delete(requestingSessionId);
            }
            return newMap;
          });

          if (apiResponse.success && apiResponse.data) {
            const taskData = apiResponse.data;
            const finalStatus = taskData.status === 'success' ? 'success' as const : 'fail' as const;
            const finalTaskCard: Message = {
              role: 'task_card',
              task_id: taskData.task_id,
              task_name: taskData.task_name || pendingTaskCard.task_name,
              status: finalStatus,
              result_info: taskData.result_info || undefined,
              error_msg: taskData.error_msg || undefined,
              created_at: pendingTaskCard.created_at,
              timestamp: new Date().toISOString(),
              params: pendingTaskCard.params
            };

            if (currentSessionIdRef.current === requestingSessionId) {
              setMessages(prev => prev.map(msg => (
                msg.task_id === pendingTaskId ? finalTaskCard : msg
              )));
            }

            if (finalStatus === 'success') {
              message.success('任务完成！');
            } else {
              message.error(`任务失败: ${taskData.error_msg || '未知错误'}`);
            }

            await loadSessions();
          } else {
            if (currentSessionIdRef.current === requestingSessionId) {
              setMessages(prev => prev.filter(msg => msg.task_id !== pendingTaskId));
            }
            message.error('任务创建失败');
            await loadSessions();
          }
        }).catch(async (err) => {
          setSessionPendingMessages(prev => {
            const newMap = new Map(prev);
            const existingMessages = newMap.get(requestingSessionId) || [];
            const filteredMessages = existingMessages.filter(msg => msg.task_id !== pendingTaskId);
            if (filteredMessages.length > 0) {
              newMap.set(requestingSessionId, filteredMessages);
            } else {
              newMap.delete(requestingSessionId);
            }
            return newMap;
          });

          if (currentSessionIdRef.current === requestingSessionId) {
            setMessages(prev => prev.filter(msg => msg.task_id !== pendingTaskId));
          }

          console.error('❌ apisource 任务请求异常:', err);
          message.error('任务请求异常');
          await loadSessions();
        });

        // apisource 分支已独立处理，跳过后续通用流程
        return;
      } else {
        // 普通单个任务创建
        // 获取当前 Tab 对应的 workflow_id（多步骤工作流时使用）
        const currentWfId = getCurrentStepWorkflowId();
        
        const requestData = {
          market_id: marketId,
          user_id: user?.user_id,
          param_data: paramData,
          session_id: currentSessionId,
          display_params,
          // 多步骤工作流时传递当前步骤的 wf_id
          ...(currentWfId ? { wf_id: currentWfId } : {}),
          // 闭源模型时传递 model_id
          ...(workflowInfo?.model_type === 'closedsource' && selectedModelId ? { model_id: selectedModelId } : {})
        };
        
        console.log('📤 发送任务创建请求:', requestData);
        
        const response = await api.post('/coze-tasks/create', requestData) as ApiResponse<any>;
        responses = [response];
      }
      
      // 处理所有响应
      const successfulTasks: any[] = [];
      const failedTasks: any[] = [];
      
      for (const response of responses) {
        if (response.success) {
          successfulTasks.push(response.data);
        } else {
          failedTasks.push(response);
        }
      }
      
      if (successfulTasks.length > 0) {
        const taskCount = successfulTasks.length;
        message.success(`成功创建 ${taskCount} 个任务！`);
        
        // 为每个成功的任务创建任务卡片
        const newTaskCards: Message[] = [];
        
        for (const taskData of successfulTasks) {
          const taskId = taskData.task_id;
          // apisource 是同步执行，后端直接返回最终 status/result_info
          // closedsource/普通工作流是异步的，设为 processing 后通过 subscribeTask 轮询
          const isApiSourceTask = workflowInfo?.model_type === 'apisource';
          const finalStatus = isApiSourceTask
            ? (taskData.status === 'success' ? 'success' : 'fail') as 'success' | 'fail'
            : 'processing' as const;
          const newTaskCard: Message = {
            role: 'task_card' as const,
            task_id: taskId,
            task_name: taskData.task_name || `任务_${taskId}`,
            status: finalStatus,
            result_info: isApiSourceTask ? (taskData.result_info || undefined) : undefined,
            error_msg: isApiSourceTask ? (taskData.error_msg || undefined) : undefined,
            created_at: new Date().toISOString(),
            timestamp: new Date().toISOString(),
            params: Object.keys(display_params).length > 0 ? display_params : null
          };
          
          console.log('📋 创建新任务卡片:', newTaskCard);
          newTaskCards.push(newTaskCard);
          
          // 仅异步任务订阅状态轮询
          if (!isApiSourceTask) {
            subscribeTask(taskId);
          }
        }
        
        // 批量添加到消息列表
        setMessages(prev => {
          console.log('📋 当前消息列表:', prev);
          const updated = [...prev, ...newTaskCards];
          console.log('📋 更新后消息列表:', updated);
          return updated;
        });
        
        // 试用用户：后端创建任务时已自动扣减使用次数，前端只需刷新显示
        if (user?.user_type === 'trial') {
          await refreshUserInfo();
        }
        
        // 刷新会话列表（标题可能已更新）
        await loadSessions();
        
        // 关闭面板并重置表单
        setComponentPanelVisible(false);
        form.resetFields();
        clearUploadedWorkflowFiles();
      }
      
      // 处理失败的任务
      if (failedTasks.length > 0) {
        console.error('❌ 部分任务创建失败:', failedTasks);
        message.error(`${failedTasks.length} 个任务创建失败`);
        
        // 积分退款逻辑已移入工作流 API 内部处理，前端不再单独退款
        // if (taskIdForPoints && workflowInfo?.model_type === 'closedsource') {
        //   // 积分退款由工作流 API 内部自动处理
        // }
      }
      
    } catch (error: any) {
      console.error('❌ [SUBMIT] 创建任务失败:', error);
      console.error('❌ [SUBMIT] 错误详情:', {
        message: error?.message,
        errorFields: error?.errorFields,
        values: error?.values
      });
      
      // 积分退款逻辑已移入工作流 API 内部处理，前端不再单独退款
      // if (taskIdForPoints && workflowInfo?.model_type === 'closedsource') {
      //   // 积分退款由工作流 API 内部自动处理
      // }
      
      if (error?.errorFields && error.errorFields.length > 0) {
        const firstError = error.errorFields[0];
        message.error(`${firstError.name[0]}: ${firstError.errors[0]}`);
        console.error('❌ [SUBMIT] 验证错误字段:', firstError);
      } else if (error?.response) {
        // 显示后端返回的错误信息
        const errorMessage = error.response?.data?.message || error.message || '创建任务失败';
        message.error(errorMessage);
        console.error('❌ [SUBMIT] 后端错误详情:', error.response?.data);
      } else {
        message.error(error?.message || '创建任务失败，请检查输入内容');
      }
    } finally {
      // 确保在任何情况下都关闭上传进度提示
      message.destroy('upload-progress');
      setSubmitting(false);
    }
  };

  // 类型验证函数
  const validateInputType = (inputType: string | undefined, value: string) => {
    if (!inputType || inputType === 'string') {
      return true; // string类型或未指定类型，不需要特殊验证
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return false; // 空值
    }

    switch (inputType) {
      case 'int':
        // 验证整数：只允许数字，可以有负号
        const intRegex = /^-?\d+$/;
        return intRegex.test(trimmedValue) && !isNaN(parseInt(trimmedValue));
      
      case 'float':
      case 'number':
        // 验证浮点数：允许小数点，可以有负号
        const floatRegex = /^-?\d*\.?\d+$/;
        return floatRegex.test(trimmedValue) && !isNaN(parseFloat(trimmedValue));
      
      default:
        return true;
    }
  };

  // 获取类型验证错误消息
  const getTypeErrorMessage = (inputType: string | undefined, fieldName: string) => {
    switch (inputType) {
      case 'int':
        return `${fieldName}必须是整数（如：123、-456）`;
      case 'float':
        return `${fieldName}必须是数字（如：123.45、-67.89）`;
      case 'number':
        return `${fieldName}必须是数字（如：123、456.78）`;
      default:
        return `${fieldName}格式不正确`;
    }
  };

  // 渲染确认对话框中的参数值
  const renderConfirmParamValue = (
    key: string,
    value: any,
    fileTypeVariables: Set<string>,
    fileNameMap: Record<string, string>,
    filePreviewUrlMap: Record<string, string> = {}
  ) => {
    const isFileParam = fileTypeVariables.has(key);
    
    // 文件类型参数
    if (isFileParam) {
      // 未上传
      if (!fileNameMap[key]) {
        return (
          <span style={{ color: '#999', fontStyle: 'italic' }}>未上传</span>
        );
      }
      
      // 已上传：提取文件名，优先使用本地预览URL（createObjectURL），否则用 value
      const fileName = fileNameMap[key];
      const fileUrl = filePreviewUrlMap[key] || (typeof value === 'string' ? value : '');
      const ext = fileName.split('.').pop()?.toLowerCase() || '';
      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
      
      const getFileIcon = (name: string) => {
        const e = name.split('.').pop()?.toLowerCase();
        switch (e) {
          case 'jpg': case 'jpeg': case 'png': case 'gif': case 'webp': case 'svg': case 'bmp': return '🖼️';
          case 'mp4': case 'avi': case 'mov': case 'webm': case 'mkv': case 'flv': case 'wmv': return '🎥';
          case 'mp3': case 'wav': case 'flac': case 'aac': case 'ogg': case 'wma': return '🎵';
          case 'pdf': return '📄';
          case 'doc': case 'docx': return '📝';
          case 'xls': case 'xlsx': return '📊';
          case 'zip': case 'rar': case '7z': case 'tar': case 'gz': return '🗜️';
          default: return '📎';
        }
      };
      
      // 图片文件：悬停显示缩略图
      if (isImage && fileUrl) {
        return (
          <Tooltip
            title={
              <div style={{ padding: '4px' }}>
                <img
                  src={fileUrl}
                  alt={fileName}
                  style={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block'
                  }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            }
            overlayStyle={{ maxWidth: '220px' }}
            placement="right"
          >
            <span style={{
              color: '#1890ff',
              cursor: 'pointer',
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <span>{getFileIcon(fileName)}</span>
              <span>{fileName}</span>
            </span>
          </Tooltip>
        );
      }
      
      // 非图片文件
      return (
        <span style={{
          color: '#1890ff',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <span>{getFileIcon(fileName)}</span>
          <span>{fileName}</span>
        </span>
      );
    }
    
    // 非文件参数：正常显示
    if (typeof value === 'string' && value.startsWith('http')) {
      return (
        <a href={value} target="_blank" rel="noopener noreferrer">
          {value.length > 60 ? value.substring(0, 60) + '...' : value}
        </a>
      );
    }
    
    // 确保返回可渲染的内容
    if (value === null || value === undefined) {
      return '';
    }
    
    // 如果是对象（比如文件上传的 fileList），转换为字符串或返回空
    if (typeof value === 'object') {
      return '';
    }
    
    // 返回原始值（字符串、数字等）
    return String(value);
  };

  // 渲染动态组件
  const renderDynamicComponent = (config: any) => {
    // 根据配置决定是否必填，默认为true保持向后兼容
    const isRequired = config.required !== undefined ? config.required : true;
    
    console.log(`🔧 [COMPONENT] 渲染组件 ${config.variable}:`, {
      component: config.component,
      name: config.name,
      required: isRequired,
      input_type: config.input_type,
      default_text: config.default_text
    });

    switch (config.component) {
      case 'textbox':
        // 构建验证规则数组
        const textboxRules: any[] = [];
        
        // 添加必填验证（如果需要）
        if (isRequired) {
          textboxRules.push({ 
            required: true, 
            message: `请输入${config.name}` 
          });
        }
        
        // 添加类型验证
        textboxRules.push({
          validator: async (_: any, value: any) => {
            console.log(`🔍 [VALIDATOR] 验证字段 ${config.variable}:`, { 
              value, 
              input_type: config.input_type,
              valueType: typeof value,
              required: isRequired
            });
            
            // 如果字段不是必填且为空，则跳过验证
            if (!isRequired && (!value || value.trim() === '')) {
              console.log(`⚠️  [VALIDATOR] ${config.variable} 非必填且为空，跳过验证`);
              return Promise.resolve();
            }
            
            // 如果字段是必填的，空值由required规则处理
            if (isRequired && (!value || value.trim() === '')) {
              console.log(`⚠️  [VALIDATOR] ${config.variable} 必填且为空，由required规则处理`);
              return Promise.resolve();
            }
            
            // 对非空值进行类型验证
            if (value && value.trim() !== '' && config.input_type && config.input_type !== 'string') {
              const isValid = validateInputType(config.input_type, value);
              console.log(`${isValid ? '✅' : '❌'} [VALIDATOR] ${config.variable} 类型验证结果:`, {
                isValid,
                input_type: config.input_type,
                value: value,
                trimmedValue: value.trim()
              });
              
              if (!isValid) {
                const errorMsg = getTypeErrorMessage(config.input_type, config.name);
                console.error(`❌ [VALIDATOR] ${config.variable} 验证失败:`, errorMsg);
                throw new Error(errorMsg);
              }
            }
            
            console.log(`✅ [VALIDATOR] ${config.variable} 验证通过`);
            return Promise.resolve();
          }
        });

        return (
          <Form.Item
            key={config.variable}
            name={config.variable}
            label={config.name}
            validateTrigger={['onChange', 'onBlur', 'onSubmit']}
            rules={textboxRules}
            style={{ marginBottom: 0 }}
            initialValue={config.default_text || undefined}
          >
            <Input.TextArea
              placeholder={
                `${isRequired ? '请输入' : '可选输入'}${config.name}${config.input_type ? ` (类型: ${config.input_type})` : ''}`
              }

              // placeholder={
              //   config.default_text 
              //     ? `默认: ${config.default_text}${config.input_type ? ` ` : ''}` 
              //     : `${isRequired ? '请输入' : '可选输入'}${config.name}${config.input_type ? ` (类型: ${config.input_type})` : ''}`
              // }
              autoSize={{ minRows: 2, maxRows: 3 }}
            />
          </Form.Item>
        );
      case 'dropdown':
        // 支持两种格式：
        // 1. 对象数组：[{ "label": "600x800 (3:4)", "value": "600x800" }]
        // 2. 字符串（兼容旧格式）：600x800|768x1280|512x512
        let dropdownOptions: Array<{ label: string; value: string }> = [];
        
        if (Array.isArray(config.values)) {
          // 新格式：对象数组
          dropdownOptions = config.values;
        } else if (typeof config.values === 'string') {
          // 旧格式：字符串分隔
          dropdownOptions = config.values.split('|').map((v: string) => ({
            label: v,
            value: v
          }));
        } else if (config.options) {
          // 兼容 options 字段
          dropdownOptions = Array.isArray(config.options) 
            ? config.options 
            : config.options.split('|').map((v: string) => ({ label: v, value: v }));
        }
        
        // 根据required字段决定默认值
        const dropdownDefaultValue = isRequired && dropdownOptions.length > 0 ? dropdownOptions[0].value : undefined;
        
        // 构建验证规则
        const dropdownRules: any[] = [];
        if (isRequired) {
          dropdownRules.push({ 
            required: true, 
            message: `请选择${config.name}` 
          });
        }
        
        return (
          <Form.Item
            key={config.variable}
            name={config.variable}
            label={config.name}
            rules={dropdownRules}
            style={{ marginBottom: 0 }}
            initialValue={dropdownDefaultValue}
          >
            <Select 
              placeholder={`${isRequired ? '请选择' : '可选择'}${config.name}`}
              allowClear={!isRequired}
            >
              {dropdownOptions.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        );
      case 'upload':
        // 支持 type 字段，格式：mp4|mkv
        const acceptTypes = config.type ? 
          config.type.split('|').map((t: string) => `.${t}`).join(',') : 
          '*';
        
        // 格式化显示的文件类型提示
        const fileTypeHint = config.type ? 
          config.type.split('|').map((t: string) => `.${t}`).join(', ') : 
          '所有文件';
        
        // 获取文件大小限制
        const maxSizeMB = config.file_size || 10;
        
        // 构建验证规则
        const uploadRules: any[] = [];
        if (isRequired) {
          uploadRules.push({ 
            required: true, 
            message: `请上传${config.name}` 
          });
        }
        
        return (
          <Form.Item
            key={config.variable}
            name={config.variable}
            label={config.name}
            rules={uploadRules}
            style={{ marginBottom: 0 }}
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e?.fileList || [];
            }}
          >
            <Upload.Dragger
              className="workflow-upload-dragger"
              maxCount={1}
              accept={acceptTypes}
              showUploadList={false}
              style={{ height: 120, minHeight: 120, maxHeight: 120, overflow: 'hidden' }}
              onChange={({ fileList }) => {
                // 当文件列表变化（包括删除），同步更新 uploadedFiles state
                if (fileList.length === 0) {
                  void handleFileChange(config.variable, null);
                }
              }}
              beforeUpload={(file) => {
                // 验证文件类型
                if (config.type) {
                  const allowedTypes = config.type.split('|');
                  const fileExt = file.name.split('.').pop()?.toLowerCase();
                  if (fileExt && !allowedTypes.includes(fileExt)) {
                    message.error(`只允许上传 ${fileTypeHint} 格式的文件`);
                    return Upload.LIST_IGNORE;
                  }
                }
                
                // 验证文件大小（根据配置或默认10MB）
                const maxSizeBytes = maxSizeMB * 1024 * 1024;
                if (file.size > maxSizeBytes) {
                  message.error(`${config.name}文件大小不能超过${maxSizeMB}MB`);
                  return Upload.LIST_IGNORE;
                }
                
                // 保存文件到 uploadedFiles
                void handleFileChange(config.variable, file);
                return false;
              }}
            >
              {uploadedFiles[config.variable] && uploadedFilePreviews[config.variable] ? (
                <div className="workflow-upload-preview">
                  <img
                    src={uploadedFilePreviews[config.variable]}
                    alt={(uploadedFiles[config.variable] as File).name}
                    className="workflow-upload-preview-image"
                  />
                  <div style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    background: 'rgba(0,0,0,0.5)',
                    borderRadius: 4,
                    padding: '2px 6px',
                    cursor: 'pointer',
                    color: '#fff',
                    fontSize: 12
                  }}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleFileChange(config.variable, null);
                    }}
                  >×</div>
                </div>
              ) : (
                <>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">点击或拖拽文件到此处上传</p>
                  <p className="ant-upload-hint">
                    {config.type ? `支持格式: ${fileTypeHint}` : '支持任意格式'}
                    {`，最大 ${maxSizeMB}MB`}
                  </p>
                </>
              )}
            </Upload.Dragger>
          </Form.Item>
        );
      case 'video_coordinate_picker':
      case 'video_region_selector':
      case 'video_coordinate':
        // 支持 type 字段，格式：mp4|mkv
        const coordinateVariables = getVideoCoordinateVariables(config);
        const coordinateAcceptTypes = config.type ?
          config.type.split('|').map((t: string) => `.${t}`).join(',') :
          '.mp4';
        const coordinateFileTypeHint = config.type ?
          config.type.split('|').map((t: string) => `.${t}`).join(', ') :
          '.mp4';
        const coordinateMaxSizeMB = config.file_size || 20;
        const coordinateUploadRules: any[] = [];
        const coordinateTextRules: any[] = [];

        if (isRequired) {
          coordinateUploadRules.push({
            required: true,
            message: `请上传${config.name}`
          });
          coordinateTextRules.push({
            required: true,
            message: `请标定${config.coordinate_label || '字幕区域坐标'}`
          });
        }

        if (!coordinateVariables.uploadVariable || !coordinateVariables.coordinateVariable) {
          return (
            <div key={config.variable} style={{ gridColumn: 'span 2' }}>
              <Text type="danger">视频坐标组件变量配置错误，请使用 ref_video_url|coordinate 格式</Text>
            </div>
          );
        }

        return (
          <div key={config.variable} style={{ gridColumn: 'span 2' }}>
            <Form.Item
              name={coordinateVariables.uploadVariable}
              label={config.name}
              rules={coordinateUploadRules}
              style={{ marginBottom: 12 }}
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e;
                }
                return e?.fileList || [];
              }}
            >
              <Upload.Dragger
                className="workflow-upload-dragger"
                maxCount={1}
                accept={coordinateAcceptTypes}
                showUploadList={false}
                style={{ height: 120, minHeight: 120, maxHeight: 120, overflow: 'hidden' }}
                onChange={({ fileList }) => {
                  // 当文件列表变化（包括删除），同步更新 uploadedFiles state
                  if (fileList.length === 0) {
                    void handleFileChange(coordinateVariables.uploadVariable, null);
                    form.setFieldsValue({ [coordinateVariables.coordinateVariable]: undefined });
                  }
                }}
                beforeUpload={(file) => {
                  // 验证文件类型
                  if (config.type) {
                    const allowedTypes = config.type.split('|');
                    const fileExt = file.name.split('.').pop()?.toLowerCase();
                    if (fileExt && !allowedTypes.includes(fileExt)) {
                      message.error(`只允许上传 ${coordinateFileTypeHint} 格式的文件`);
                      return Upload.LIST_IGNORE;
                    }
                  }

                  // 验证文件大小（根据配置或默认20MB）
                  const maxSizeBytes = coordinateMaxSizeMB * 1024 * 1024;
                  if (file.size > maxSizeBytes) {
                    message.error(`${config.name}文件大小不能超过${coordinateMaxSizeMB}MB`);
                    return Upload.LIST_IGNORE;
                  }

                  // 保存文件并清空旧坐标，避免视频和坐标不匹配
                  void handleFileChange(coordinateVariables.uploadVariable, file);
                  form.setFieldsValue({ [coordinateVariables.coordinateVariable]: undefined });
                  return false;
                }}
              >
                {uploadedFiles[coordinateVariables.uploadVariable] && uploadedFilePreviews[coordinateVariables.uploadVariable] ? (
                  <div className="workflow-upload-preview">
                    <img
                      src={uploadedFilePreviews[coordinateVariables.uploadVariable]}
                      alt={(uploadedFiles[coordinateVariables.uploadVariable] as File).name}
                      className="workflow-upload-preview-image"
                    />
                    <div style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      background: 'rgba(0,0,0,0.5)',
                      borderRadius: 4,
                      padding: '2px 6px',
                      cursor: 'pointer',
                      color: '#fff',
                      fontSize: 12
                    }}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleFileChange(coordinateVariables.uploadVariable, null);
                        form.setFieldsValue({ [coordinateVariables.coordinateVariable]: undefined });
                      }}
                    >×</div>
                  </div>
                ) : (
                  <>
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">点击或拖拽视频到此处上传</p>
                    <p className="ant-upload-hint">
                      {`支持格式: ${coordinateFileTypeHint}，最大 ${coordinateMaxSizeMB}MB`}
                    </p>
                  </>
                )}
              </Upload.Dragger>
            </Form.Item>

            <Form.Item
              name={coordinateVariables.coordinateVariable}
              label={config.coordinate_label || '字幕区域坐标'}
              rules={coordinateTextRules}
              style={{ marginBottom: 0 }}
            >
              <Input.TextArea
                readOnly
                placeholder="点击右侧按钮，在视频中框选字幕区域"
                autoSize={{ minRows: 2, maxRows: 3 }}
              />
            </Form.Item>

            <Button
              type="default"
              icon={<ToolOutlined />}
              onClick={() => void openVideoCoordinatePicker(config)}
              disabled={!uploadedFiles[coordinateVariables.uploadVariable]}
              style={{ marginTop: 8 }}
            >
              标定字幕区域
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="ai-conversation-page">
      {/* 会话侧边栏 */}
      <SessionSidebar
        sessions={sessions}
        currentSessionId={currentSessionId}
        loading={sessionsLoading}
        onCreateSession={handleCreateSession}
        onSelectSession={handleSelectSession}
        onDeleteSession={handleDeleteSession}
      />

      {/* 主内容区 */}
      <div className="conversation-main">
        {/* 头部 */}
        <div className="conversation-header">
          <Space>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => {
                // 尝试从 sessionStorage 恢复工作流市场页面状态
                const savedState = sessionStorage.getItem('workflowMarketState');
                if (savedState) {
                  try {
                    const state = JSON.parse(savedState);
                    const params = new URLSearchParams();
                    if (state.page && state.page !== '1') params.set('page', state.page);
                    if (state.pageSize && state.pageSize !== '12') params.set('pageSize', state.pageSize);
                    if (state.search) params.set('search', state.search);
                    if (state.category && state.category !== 'all') params.set('category', state.category);
                    
                    const queryString = params.toString();
                    navigate(`/workflow-market${queryString ? '?' + queryString : ''}`);
                  } catch (e) {
                    navigate('/workflow-market');
                  }
                } else {
                  navigate('/workflow-market');
                }
              }}
            >
              返回
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              {workflowInfo?.display_name || '大模型对话'}
            </Title>
          </Space>
          
          {/* 右侧按钮组 */}
          <Space>
            <Button 
              icon={<QuestionCircleOutlined />} 
              onClick={showInstructionsModal}
              type="default"
            >
              使用说明
            </Button>
          </Space>
        </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* 动态组件参数面板 - 浮层样式 */}
      {componentPanelVisible && workflowInfo?.component_template && workflowInfo.component_template.length > 0 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <Card 
            className="component-panel"
            style={{
              width: '60%',
              maxWidth: '800px',
              maxHeight: '80vh',
              overflow: 'auto',
              borderRadius: '12px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              animation: 'slideDown 0.3s ease-out'
            }}
            extra={
              <Button 
                type="text" 
                icon={<CloseOutlined />} 
                onClick={() => setComponentPanelVisible(false)}
                style={{ color: '#999' }}
              />
            }
            title={
              <Space>
                <ToolOutlined style={{ color: '#1890ff' }} />
                <span>工作流参数配置</span>
              </Space>
            }
          >
            <Form form={form} layout="vertical">
              {/* apisource 渠道分组选择（第一行） */}
              {workflowInfo.model_type === 'apisource' && callGroups.length > 0 && (
                <Form.Item
                  label={
                    <Space>
                      <span>选择模型渠道</span>
                      <span style={{ fontSize: '12px', color: '#999' }}>
                        (当前积分: {userPoints})
                      </span>
                    </Space>
                  }
                  required
                  style={{ marginBottom: '24px' }}
                >
                  <Select
                    value={selectedGroupId}
                    onChange={setSelectedGroupId}
                    placeholder="请选择模型渠道"
                    size="large"
                    optionLabelProp="label"
                  >
                    {callGroups.map((group: CallGroup) => {
                      const pts = getCallGroupPreDeductedPoints(group);
                      const labelText = group.group_name;
                      return (
                        <Select.Option key={group.group_id} value={group.group_id} label={labelText}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{group.group_name}</span>
                            {pts != null && pts > 0 && (
                              <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                                {pts} 积分
                              </span>
                            )}
                          </div>
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              )}

              {/* 闭源模型渠道选择（第一行） */}
              {workflowInfo.model_type === 'closedsource' && availableModels.length > 0 && (
                <Form.Item
                  label={
                    <Space>
                      <span>选择模型渠道</span>
                      <span style={{ fontSize: '12px', color: '#999' }}>
                        (当前积分: {userPoints})
                      </span>
                    </Space>
                  }
                  required
                  style={{ marginBottom: '24px' }}
                >
                  <Select
                    value={selectedModelId}
                    onChange={setSelectedModelId}
                    placeholder="请选择模型渠道"
                    size="large"
                    optionLabelProp="label"
                  >
                    {availableModels.map((model: any) => {
                      // 强制转为布尔值，避免 0 被显示
                      const isDefault = Boolean(model.is_default);
                      const labelText = `${model.display_name || model.model_id}${isDefault ? ' (推荐)' : ''}`;
                      return (
                        <Select.Option key={model.model_id} value={model.model_id} label={labelText}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>
                              {model.display_name || model.model_id}
                              {isDefault && <span style={{ color: '#1890ff', marginLeft: '8px' }}>(推荐)</span>}
                            </span>
                            <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                              {model.cost_points ?? 0} 积分
                            </span>
                          </div>
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              )}
              
              {/* 多步骤工作流 Tab 分页显示 */}
              {isMultiStepWorkflow(workflowInfo.component_template) ? (
                <Tabs
                  activeKey={activeStepKey}
                  onChange={setActiveStepKey}
                  type="card"
                  style={{ marginBottom: '16px' }}
                  items={(workflowInfo.component_template as WorkflowStepConfig[]).map((step) => ({
                    key: step.page_id,
                    label: step.title,
                    children: (
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(2, 1fr)', 
                        gap: '16px',
                        marginBottom: '16px'
                      }}>
                        {step.fields.map((config: ComponentConfig) => renderDynamicComponent(config))}
                      </div>
                    )
                  }))}
                />
              ) : (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(2, 1fr)', 
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  {getCurrentStepFields().map((config: ComponentConfig) => renderDynamicComponent(config))}
                </div>
              )}
              
              <Form.Item style={{ marginBottom: 0 }}>
                <Button 
                  type="primary" 
                  onClick={handleCreateTask}
                  block
                  size="large"
                  style={{ borderRadius: '8px' }}
                  loading={submitting}
                >
                  创建任务
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </div>
      )}

      {/* 消息列表 */}
      <div className="messages-container">
        {messages.length === 0 && (
          <div className="empty-messages">
            {/* 欢迎引导页 */}
            <div style={{ textAlign: 'center', width: '100%', maxWidth: '640px', padding: '0 24px' }}>
              {/* 顶部图标 */}
              <div style={{
                width: 72,
                height: 72,
                borderRadius: 20,
                background: 'var(--primary-gradient, linear-gradient(135deg, #7c3aed, #5b21b6))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                fontSize: 32,
                boxShadow: '0 8px 24px rgba(124,58,237,0.3)'
              }}>
                😊
              </div>

              {/* 标题 */}
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>
                准备开始你的创意之旅！
              </div>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 32 }}>
                选择下方引导快速开始，或直接在输入框中提问
              </div>

              {/* 两个功能卡片 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 32 }}>
                {/* 创建新对话卡片 */}
                <div
                  onClick={handleCreateSession}
                  style={{
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 12,
                    padding: '20px 18px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--primary-color)';
                    (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 16px rgba(124,58,237,0.12)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border-color)';
                    (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: 'var(--primary-gradient, linear-gradient(135deg, #7c3aed, #5b21b6))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 12,
                    color: 'white',
                    fontSize: 18,
                    fontWeight: 700
                  }}>
                    +
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>
                    创建新对话
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                    点击左上角「New Chat」按钮，创建一个全新的对话线程，开始独立的工作流任务
                  </div>
                </div>

                {/* 查看使用说明卡片 */}
                <div
                  onClick={showInstructionsModal}
                  style={{
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 12,
                    padding: '20px 18px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--primary-color)';
                    (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 16px rgba(124,58,237,0.12)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border-color)';
                    (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: '#f3f0ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 12,
                    color: 'var(--primary-color)',
                    fontSize: 18
                  }}>
                    <QuestionCircleOutlined />
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>
                    查看使用说明
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                    点击右上角「使用说明」按钮，了解工作流的完整操作流程与使用技巧
                  </div>
                </div>
              </div>

              {/* 快捷操作提示 */}
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 8 }}>快捷操作</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 20, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  <kbd style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 4,
                    fontSize: 11,
                    fontFamily: 'monospace',
                    marginRight: 6
                  }}>Enter</kbd>
                  发送消息
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  <kbd style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 4,
                    fontSize: 11,
                    fontFamily: 'monospace',
                    marginRight: 6
                  }}>Shift + Enter</kbd>
                  换行
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  <kbd style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 4,
                    fontSize: 11,
                    fontFamily: 'monospace',
                    marginRight: 6
                  }}>↑</kbd>
                  历史对话
                </span>
              </div>
            </div>
          </div>
        )}

        {/* 消息和任务卡片混合列表 */}
        {messages.map((msg, index) => {
          // 如果是任务卡片，渲染 TaskCard 组件（带助手头像）- 单层结构
          if (msg.role === 'task_card') {
            // 格式化时间
            const formatTime = (dateStr: string) => {
              const date = new Date(dateStr);
              const hours = date.getHours().toString().padStart(2, '0');
              const minutes = date.getMinutes().toString().padStart(2, '0');
              return `${hours}:${minutes}`;
            };
            
            return (
              <div 
                key={msg.task_id || index}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  marginBottom: '16px',
                  gap: '12px'
                }}
              >
                <Avatar 
                  style={{ 
                    backgroundColor: '#52c41a',
                    flexShrink: 0
                  }}
                >
                  <RobotOutlined />
                </Avatar>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  maxWidth: '70%'
                }}>
                  <div className="message-item">
                    <TaskCard
                      taskId={msg.task_id!}
                      taskName={msg.task_name!}
                      status={msg.status!}
                      resultInfo={msg.result_info}
                      errorMsg={msg.error_msg}
                      createdAt={msg.created_at || msg.timestamp}
                      onUrlInvalid={handleUrlInvalid}
                      params={msg.params}
                    />
                  </div>
                  <Text 
                    type="secondary" 
                    style={{
                      fontSize: '12px',
                      marginTop: '4px',
                      color: '#8c8c8c'
                    }}
                  >
                    {formatTime(msg.created_at || msg.timestamp)}
                  </Text>
                </div>
              </div>
            );
          }
          
          // 否则渲染普通消息 - 单层div结构
          return (
          <div 
            key={index} 
            style={{
              display: 'flex',
              flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
              alignItems: 'flex-start',
              marginBottom: '16px',
              gap: '12px'
            }}
          >
            <Avatar 
              style={{ 
                backgroundColor: msg.role === 'user' ? 'linear-gradient(to right, #4F46E5, #7C3AED)' : '#52c41a',
                flexShrink: 0
              }}
            >
              {msg.role === 'user' ? getUserAvatar() : <RobotOutlined />}
            </Avatar>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '70%'
            }}>
              <div 
                className={`message-item ${msg.role === 'user' ? 'user-message' : 'assistant-message'}`}
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {/* 如果有图片，先显示图片 */}
                {msg.image_urls && msg.image_urls.length > 0 && (
                  <div style={{ marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {msg.image_urls.map((url, idx) => (
                      <div
                        key={idx}
                        style={{
                          maxWidth: '200px',
                          maxHeight: '200px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          flexShrink: 0,
                          display: 'inline-block'
                        }}
                      >
                        <img
                          src={url}
                          alt={`uploaded-${idx}`}
                          style={{
                            display: 'block',
                            maxWidth: '200px',
                            maxHeight: '200px',
                            width: 'auto',
                            height: 'auto',
                            borderRadius: '8px',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
                {msg.content}
              </div>
              <Text 
                type="secondary" 
                style={{
                  fontSize: '12px',
                  marginTop: '4px',
                  color: '#8c8c8c'
                }}
              >
                {msg.timestamp && !isNaN(new Date(msg.timestamp).getTime()) 
                  ? new Date(msg.timestamp).toLocaleTimeString('zh-CN', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : ''
                }
              </Text>
            </div>
          </div>
          );
        })}

        {loading && loadingSessionId === currentSessionId && (
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            marginBottom: '16px',
            gap: '12px'
          }}>
            <Avatar 
              style={{ backgroundColor: '#52c41a', flexShrink: 0 }}
            >
              <RobotOutlined />
            </Avatar>
            <div className="message-item assistant-message">
              <Spin size="small" /> 正在思考...
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* 输入框 */}
      <div 
        className="input-container"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {/* 多图片预览 */}
        {uploadedImages.length > 0 && (
          <div className="image-preview" style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '8px',
            padding: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            {uploadedImages.map((imageUrl, index) => (
              <div key={index} style={{ position: 'relative' }}>
                <RetryImage 
                  src={imageUrl} 
                  alt={`uploaded-${index}`}
                  maxRetries={0}
                  retryDelay={0}
                  style={{
                    width: '80px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                  }}
                />
                <Button 
                  type="text" 
                  danger 
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveImage(index)}
                  size="small"
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    padding: '2px',
                    minWidth: '24px',
                    height: '24px'
                  }}
                />
              </div>
            ))}
            <div style={{ 
              fontSize: '12px', 
              color: '#8c8c8c',
              alignSelf: 'center',
              marginLeft: '8px'
            }}>
              {uploadedImages.length}/{maxImages}
            </div>
          </div>
        )}

        <div className="input-wrapper" style={{ position: 'relative' }}>
          {/* @mention下拉列表 */}
          {showMentionDropdown && filteredMentions.length > 0 && (
            <div className="mention-dropdown">
              {filteredMentions.map((item, index) => (
                <div
                  key={item.id}
                  className={`mention-item ${index === selectedMentionIndex ? 'mention-item-selected' : ''}`}
                  onClick={() => handleSelectMention(item.mention_tag)}
                  style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                    <div className="mention-tag">{item.mention_tag}</div>
                    <div className="mention-display-name">{item.model_config.display_name}</div>
                  </div>
                  <div style={{ fontSize: '13px', color: '#52c41a', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0, marginLeft: '12px' }}>
                    {item.model_config.cost_type === 'free' ? '免费' : 
                     item.model_config.cost_type === 'dynamic' ? '动态计费' :
                     `${item.model_config.cost_points}积分/次`}
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* 图片上传按钮 */}
          <Upload
            beforeUpload={handleImageUpload}
            showUploadList={false}
            accept="image/jpeg,image/jpg,image/png"
          >
            <Button
              icon={<PaperClipOutlined />}
              disabled={loading || uploading}
              loading={uploading}
            />
          </Upload>

          {/* 输入框 */}
          <TextArea
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onKeyPress={handleKeyPress}
            // placeholder="输入消息... (输入@选择模型，↑↓选择，Enter确认，Shift+Enter换行)"
            placeholder="请输入消息..."
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={loading}
          />

          {/* 发送按钮 */}
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={loading}
            disabled={!inputValue.trim()}
          >
            发送
          </Button>
        </div>
      </div>

      {/* 右下角圆形按钮 */}
      {workflowInfo?.component_template && workflowInfo.component_template.length > 0 && !componentPanelVisible && (
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<PlusOutlined />}
          onClick={handleToggleComponentPanel}
          style={{
            position: 'fixed',
            right: '32px',
            bottom: '100px',
            width: '56px',
            height: '56px',
            fontSize: '24px',
            boxShadow: '0 4px 16px rgba(24, 144, 255, 0.4)',
            zIndex: 1300,
            animation: 'pulse 2s infinite'
          }}
        />
      )}

      {/* 视频坐标标定弹窗 */}
      <Modal
        title="标定字幕区域"
        open={!!videoCoordinatePicker}
        onCancel={closeVideoCoordinatePicker}
        onOk={confirmVideoCoordinateSelection}
        okText="确定"
        cancelText="取消"
        width={900}
        centered
        destroyOnClose
      >
        {videoCoordinatePicker && (
          <div>
            <div style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
              <div>文件：{videoCoordinatePicker.fileName}</div>
              <div>
                原始分辨率：{videoCoordinatePicker.sourceWidth}×{videoCoordinatePicker.sourceHeight}
                {' / '}
                坐标分辨率：{Math.round(videoCoordinatePicker.normalizedWidth)}×{Math.round(videoCoordinatePicker.normalizedHeight)}
                {' / '}
                最长边：{videoCoordinatePicker.longest}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <div style={{ maxWidth: '100%', width: 'fit-content' }}>
                <div
                  ref={videoPickerFrameRef}
                  style={{
                    position: 'relative',
                    maxWidth: '100%',
                    width: 'fit-content',
                    background: '#000',
                    userSelect: 'none'
                  }}
                >
                  <video
                    ref={videoPickerVideoRef}
                    src={videoCoordinatePicker.videoUrl}
                    preload="metadata"
                    onLoadedMetadata={(event) => {
                      setVideoPickerDuration(event.currentTarget.duration || 0);
                      setVideoPickerCurrentTime(event.currentTarget.currentTime || 0);
                    }}
                    onTimeUpdate={(event) => {
                      setVideoPickerCurrentTime(event.currentTarget.currentTime || 0);
                    }}
                    onPlay={() => setIsVideoPickerPlaying(true)}
                    onPause={() => setIsVideoPickerPlaying(false)}
                    onEnded={() => setIsVideoPickerPlaying(false)}
                    style={{
                      display: 'block',
                      maxWidth: '100%',
                      maxHeight: '65vh'
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      cursor: isMovingVideoRegion || activeVideoResizeHandle ? 'grabbing' : 'crosshair'
                    }}
                    onMouseDown={handleVideoRegionMouseDown}
                    onMouseMove={handleVideoRegionMouseMove}
                    onMouseUp={handleVideoRegionMouseUp}
                    onMouseLeave={handleVideoRegionMouseUp}
                  >
                    {videoSelectionRect && (() => {
                      const bounds = getVideoSelectionBounds(videoSelectionRect);
                      const handleSize = 14;
                      const handles: Array<{ key: VideoResizeHandle; left: number; top: number; cursor: string }> = [
                        { key: 'top-left', left: bounds.left, top: bounds.top, cursor: 'nwse-resize' },
                        { key: 'top-right', left: bounds.right, top: bounds.top, cursor: 'nesw-resize' },
                        { key: 'bottom-left', left: bounds.left, top: bounds.bottom, cursor: 'nesw-resize' },
                        { key: 'bottom-right', left: bounds.right, top: bounds.bottom, cursor: 'nwse-resize' }
                      ];

                      return (
                        <>
                          <div
                            style={{
                              position: 'absolute',
                              left: bounds.left,
                              top: bounds.top,
                              width: bounds.width,
                              height: bounds.height,
                              border: '2px solid #ff4d4f',
                              background: 'rgba(255, 77, 79, 0.16)',
                              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.18)',
                              cursor: isMovingVideoRegion ? 'grabbing' : 'move',
                              pointerEvents: 'auto'
                            }}
                            onMouseDown={handleVideoSelectionMouseDown}
                          />
                          {handles.map((handle) => (
                            <div
                              key={handle.key}
                              onMouseDown={(event) => handleVideoResizeHandleMouseDown(event, handle.key)}
                              style={{
                                position: 'absolute',
                                left: handle.left - handleSize / 2,
                                top: handle.top - handleSize / 2,
                                width: handleSize,
                                height: handleSize,
                                borderRadius: '50%',
                                border: '2px solid #fff',
                                background: '#ff4d4f',
                                boxShadow: '0 1px 6px rgba(0, 0, 0, 0.35)',
                                cursor: handle.cursor,
                                zIndex: 2
                              }}
                            />
                          ))}
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  background: '#f5f5f5',
                  border: '1px solid #e5e7eb',
                  borderTop: 'none'
                }}>
                  <Button
                    type="text"
                    icon={isVideoPickerPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                    onClick={toggleVideoPickerPlayback}
                    style={{ flex: '0 0 auto' }}
                  />
                  <Text style={{ flex: '0 0 auto', fontVariantNumeric: 'tabular-nums' }}>
                    {formatVideoPickerTime(videoPickerCurrentTime)} / {formatVideoPickerTime(videoPickerDuration)}
                  </Text>
                  <Slider
                    min={0}
                    max={videoPickerDuration || 0}
                    step={0.01}
                    value={Math.min(videoPickerCurrentTime, videoPickerDuration || 0)}
                    onChange={seekVideoPicker}
                    disabled={!videoPickerDuration}
                    tooltip={{ formatter: (value) => formatVideoPickerTime(value || 0) }}
                    style={{ flex: 1, margin: 0 }}
                  />
                </div>
              </div>
            </div>

            <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
              在视频画面上按住鼠标拖拽，框选需要传入工作流的字幕区域。
            </Text>
          </div>
        )}
      </Modal>

      {/* 使用说明弹窗 */}
      <Modal
        title={
          <Space>
            <QuestionCircleOutlined style={{ color: '#1890ff' }} />
            <span>使用说明 - {workflowInfo?.display_name || '工作流'}</span>
          </Space>
        }
        open={isInstructionsModalVisible}
        onCancel={handleInstructionsModalClose}
        footer={[
          <Button key="close" onClick={handleInstructionsModalClose}>
            关闭
          </Button>
        ]}
        width={700}
        centered
      >
        <div 
          style={{ 
            maxHeight: '70vh', 
            overflow: 'auto',
            padding: '16px 0'
          }}
        >
          {instructionsContent ? (
            <div 
              className="instructions-content"
              dangerouslySetInnerHTML={{ __html: instructionsContent }}
              style={{
                lineHeight: '1.6'
              }}
            />
          ) : (
            <div style={{ 
              textAlign: 'center', 
              color: '#999', 
              padding: '40px 0' 
            }}>
              <QuestionCircleOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
              <p>暂无使用说明</p>
            </div>
          )}
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default AIConversationPage;
